// SportsArena — the on-quad open-air arena.
//
// This component owns:
//   • The court / pitch visuals for the currently active sport
//   • The ball (mesh only — physics is driven by useArenaPhysics)
//   • Four blocky, Roblox-style opponent NPCs with chase-and-shoot AI
//   • The invisible ground-plane click catcher used for player kicks
//   • A "GOAL!" callout billboard that flashes briefly on each score
//
// One active sport at a time, gated by props.sport. Switching sport rebuilds
// the ball body (see useArenaPhysics) and re-parents the arena geometry.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Text, Html } from "@react-three/drei";
import * as THREE from "three";

import {
  ARENA_CENTER,
  ENTRANCE_CENTER,
  SPORT_CENTERS,
  getSportCenter,
  BASKETBALL_COURT,
  SOCCER_PITCH,
  FOOTBALL_FIELD,
  BASEBALL_DIAMOND,
  SPORTS,
  DIFFICULTIES,
  TEAM_COLORS,
  PLAYER_KICK_RADIUS,
  NPC_KICK_RADIUS,
} from "./arenaState";
import { useArenaPhysics } from "./useArenaPhysics";
import { playCheer, playWhistle, playSwoosh, playBatCrack } from "./cheerSfx";
import { useArenaPresence } from "./useArenaPresence";
import { getTeamColorById } from "./arenaState";
import SpectatorCrowd from "./SpectatorCrowd";

// ---------------------------------------------------------------------------
// Basketball court visuals
// ---------------------------------------------------------------------------
function BasketballCourt({ teamColor }) {
  const [ax, , az] = SPORT_CENTERS.basketball;
  const { length, width, hoopHeight, hoopReach, colors } = BASKETBALL_COURT;
  return (
    <group position={[ax, 0, az]}>
      {/* Painted floor */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <planeGeometry args={[length, width]} />
        <meshStandardMaterial color={colors.floor} roughness={0.75} />
      </mesh>
      {/* Border stripe */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]}>
        <ringGeometry args={[Math.min(length, width) / 2 - 0.2, Math.min(length, width) / 2, 32]} />
        <meshBasicMaterial color={colors.line} />
      </mesh>
      {/* Centre circle */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.031, 0]}>
        <ringGeometry args={[1.65, 1.85, 32]} />
        <meshBasicMaterial color={colors.line} />
      </mesh>
      {/* Half-court line */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.031, 0]}>
        <planeGeometry args={[0.18, width - 0.6]} />
        <meshBasicMaterial color={colors.line} />
      </mesh>
      {/* Painted key on each side */}
      {[-1, 1].map((sign) => (
        <mesh
          key={sign}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[sign * (length / 2 - 2.2), 0.033, 0]}
        >
          <planeGeometry args={[3.8, 3.6]} />
          <meshStandardMaterial color={colors.key} roughness={0.9} opacity={0.85} transparent />
        </mesh>
      ))}
      {/* Two hoops — player side (sign === −1) picks up the team colour on
          the rim + shooter's square so it reads as "your" basket. */}
      {[-1, 1].map((sign) => (
        <BasketballHoop
          key={sign}
          side={sign}
          hoopHeight={hoopHeight}
          x={sign * hoopReach}
          netColor={sign === -1 ? teamColor : "#ffffff"}
          rimColor={sign === -1 ? teamColor : "#f26a34"}
        />
      ))}
    </group>
  );
}

function BasketballHoop({ side, hoopHeight, x, netColor = "#ffffff", rimColor = "#f26a34" }) {
  // The rim & backboard face inward toward centre court. `side` = ±1 tells
  // us which way "inward" is; `x` is the outer anchor on the court's X axis
  // (± hoopReach). The rim sits 0.15m inward from the anchor so backboards
  // stand *behind* the rim in the traditional NBA/NCAA layout.
  const rimInward = -side * 0.15;
  return (
    <group position={[x, 0, 0]}>
      {/* Support pole — chunkier so it reads from a distance */}
      <mesh castShadow position={[side * 0.55, hoopHeight / 2 + 0.35, 0]}>
        <cylinderGeometry args={[0.16, 0.19, hoopHeight + 0.7, 12]} />
        <meshStandardMaterial color="#3f4b66" roughness={0.6} />
      </mesh>
      {/* Short horizontal arm from pole to backboard for realism */}
      <mesh castShadow position={[side * 0.35, hoopHeight + 0.55, 0]}>
        <boxGeometry args={[0.4, 0.14, 0.14]} />
        <meshStandardMaterial color="#3f4b66" roughness={0.6} />
      </mesh>

      {/* Backboard — white acrylic slab, taller so it reads clearly */}
      <mesh
        castShadow
        position={[side * 0.15, hoopHeight + 0.55, 0]}
        rotation={[0, side === -1 ? 0 : Math.PI, 0]}
      >
        <boxGeometry args={[0.09, 1.35, 2.05]} />
        <meshStandardMaterial color="#f7f5ee" roughness={0.35} metalness={0.05} />
      </mesh>
      {/* Backboard frame — thin coloured border for visibility */}
      <mesh
        position={[side * 0.10, hoopHeight + 0.55, 0]}
        rotation={[0, side === -1 ? 0 : Math.PI, 0]}
      >
        <boxGeometry args={[0.04, 1.42, 2.12]} />
        <meshBasicMaterial color="#0b1530" />
      </mesh>
      {/* Shooter's square — the small painted target directly above the rim.
          Sits on the inward face of the backboard, 0.02m proud so it doesn't
          z-fight with the acrylic slab behind it. */}
      <mesh
        position={[side * 0.10 - side * 0.02, hoopHeight + 0.2, 0]}
        rotation={[0, side === -1 ? 0 : Math.PI, 0]}
      >
        <boxGeometry args={[0.02, 0.5, 0.75]} />
        <meshBasicMaterial color={rimColor} />
      </mesh>

      {/* Rim — thicker torus so it visibly holds the ball */}
      <mesh
        position={[rimInward, hoopHeight, 0]}
        rotation={[Math.PI / 2, 0, 0]}
        castShadow
      >
        <torusGeometry args={[0.42, 0.055, 12, 28]} />
        <meshStandardMaterial
          color={rimColor}
          metalness={0.55}
          roughness={0.32}
          emissive={rimColor}
          emissiveIntensity={0.15}
        />
      </mesh>
      {/* Net — layered wireframe cone + soft solid inner so the net reads
          from any camera angle, not just against dark backgrounds. */}
      <mesh position={[rimInward, hoopHeight - 0.32, 0]}>
        <coneGeometry args={[0.4, 0.62, 16, 1, true]} />
        <meshBasicMaterial color={netColor} wireframe transparent opacity={0.85} />
      </mesh>
      <mesh position={[rimInward, hoopHeight - 0.32, 0]}>
        <coneGeometry args={[0.36, 0.55, 16, 1, true]} />
        <meshBasicMaterial color={netColor} transparent opacity={0.15} />
      </mesh>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Soccer pitch visuals
// ---------------------------------------------------------------------------
function SoccerPitch({ teamColor }) {
  const [ax, , az] = SPORT_CENTERS.soccer;
  const { length, width, goalReach, goalWidth, goalHeight, goalDepth, colors } =
    SOCCER_PITCH;
  return (
    <group position={[ax, 0, az]}>
      {/* Turf */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <planeGeometry args={[length, width]} />
        <meshStandardMaterial color={colors.turf} roughness={0.95} />
      </mesh>
      {/* Alternating stripes for playful readability */}
      {Array.from({ length: 6 }).map((_, i) => {
        const x = (i - 2.5) * (length / 6);
        if (i % 2 === 0) return null;
        return (
          <mesh
            key={i}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[x, 0.021, 0]}
          >
            <planeGeometry args={[length / 6, width]} />
            <meshStandardMaterial
              color={colors.turfAlt}
              roughness={0.95}
              transparent
              opacity={0.5}
            />
          </mesh>
        );
      })}
      {/* Centre circle */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.031, 0]}>
        <ringGeometry args={[1.9, 2.1, 32]} />
        <meshBasicMaterial color={colors.line} />
      </mesh>
      {/* Halfway line */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.031, 0]}>
        <planeGeometry args={[0.2, width - 0.4]} />
        <meshBasicMaterial color={colors.line} />
      </mesh>
      {/* Two goals — the player-side (sign === −1) inherits the team colour
          on BOTH posts and net so the visitor can see their end at a glance. */}
      {[-1, 1].map((sign) => (
        <SoccerGoal
          key={sign}
          side={sign}
          x={sign * goalReach}
          w={goalWidth}
          h={goalHeight}
          d={goalDepth}
          netColor={sign === -1 ? teamColor : "#ffffff"}
          postColor={sign === -1 ? teamColor : "#ffffff"}
        />
      ))}
    </group>
  );
}

function SoccerGoal({ side, x, w, h, d, netColor = "#ffffff", postColor = "#ffffff" }) {
  // Goal frame = two posts + crossbar. The mouth faces inward (toward -side*X).
  const postR = 0.06;
  return (
    <group position={[x, 0, 0]}>
      {/* Left post */}
      <mesh castShadow position={[0, h / 2, -w / 2]}>
        <cylinderGeometry args={[postR, postR, h, 10]} />
        <meshStandardMaterial color={postColor} roughness={0.4} emissive={postColor} emissiveIntensity={postColor === "#ffffff" ? 0 : 0.25} />
      </mesh>
      {/* Right post */}
      <mesh castShadow position={[0, h / 2, w / 2]}>
        <cylinderGeometry args={[postR, postR, h, 10]} />
        <meshStandardMaterial color={postColor} roughness={0.4} emissive={postColor} emissiveIntensity={postColor === "#ffffff" ? 0 : 0.25} />
      </mesh>
      {/* Crossbar */}
      <mesh castShadow position={[0, h, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[postR, postR, w, 10]} />
        <meshStandardMaterial color={postColor} roughness={0.4} emissive={postColor} emissiveIntensity={postColor === "#ffffff" ? 0 : 0.25} />
      </mesh>
      {/* Net — a translucent boxy volume behind the goal line */}
      <mesh position={[side * (d / 2), h / 2, 0]}>
        <boxGeometry args={[d, h, w]} />
        <meshBasicMaterial color={netColor} wireframe transparent opacity={0.65} />
      </mesh>
    </group>
  );
}

// ---------------------------------------------------------------------------
// The ball — mesh only. Rapier body drives position/rotation.
// Football gets a stretched ellipsoid mesh via non-uniform scale on a sphere.
// ---------------------------------------------------------------------------
const Ball = React.forwardRef(function Ball({ sport }, ref) {
  const cfg = SPORTS[sport];
  const [cx, , cz] = getSportCenter(sport);
  if (sport === "football") {
    return (
      <mesh
        ref={ref}
        castShadow
        position={[cx, 1.2, cz]}
        scale={[1.85, 1.0, 1.0]}
      >
        <sphereGeometry args={[cfg.ballRadius, 20, 14]} />
        <meshStandardMaterial
          color={cfg.ballColor}
          roughness={0.7}
          metalness={0.02}
        />
        {/* Laces — a small white stripe on the top */}
        <mesh position={[0, cfg.ballRadius * 0.7, 0]} scale={[0.45, 0.05, 0.15]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color={cfg.ballAccent} />
        </mesh>
      </mesh>
    );
  }
  return (
    <mesh
      ref={ref}
      castShadow
      position={[cx, 2.6, cz]}
    >
      <sphereGeometry args={[cfg.ballRadius, 22, 22]} />
      <meshStandardMaterial
        color={cfg.ballColor}
        roughness={sport === "basketball" ? 0.55 : 0.6}
        metalness={0.05}
      />
      {sport === "basketball" && (
        <mesh scale={[1.001, 1.001, 1.001]}>
          <sphereGeometry
            args={[cfg.ballRadius, 22, 22, 0, Math.PI * 2, 0, Math.PI]}
          />
          <meshBasicMaterial color={cfg.ballAccent} wireframe opacity={0.35} transparent />
        </mesh>
      )}
    </mesh>
  );
});

// ---------------------------------------------------------------------------
// FootballField — gridiron with two coloured end zones, yard lines, hash marks.
// ---------------------------------------------------------------------------
function FootballField({ teamColor }) {
  const [ax, , az] = SPORT_CENTERS.football;
  const { length, width, endZoneDepth, colors } = FOOTBALL_FIELD;
  const yardLineCount = 9;
  return (
    <group position={[ax, 0, az]}>
      {/* Playing field turf */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <planeGeometry args={[length - 2 * endZoneDepth, width]} />
        <meshStandardMaterial color={colors.turf} roughness={0.95} />
      </mesh>
      {/* Alternating stripes for the mowed-grass look */}
      {Array.from({ length: 6 }).map((_, i) => {
        if (i % 2 === 0) return null;
        const seg = (length - 2 * endZoneDepth) / 6;
        const x = (i - 2.5) * seg;
        return (
          <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.021, 0]}>
            <planeGeometry args={[seg, width]} />
            <meshStandardMaterial color={colors.turfAlt} roughness={0.95} transparent opacity={0.55} />
          </mesh>
        );
      })}
      {/* Player end zone (visitor scores here — CPU end zone, on +X side) */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[length / 2 - endZoneDepth / 2, 0.022, 0]}>
        <planeGeometry args={[endZoneDepth, width]} />
        <meshStandardMaterial color={teamColor || colors.endZone} roughness={0.9} />
      </mesh>
      {/* CPU end zone (visitor defends this) */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[-length / 2 + endZoneDepth / 2, 0.022, 0]}>
        <planeGeometry args={[endZoneDepth, width]} />
        <meshStandardMaterial color={colors.endZoneAway} roughness={0.9} />
      </mesh>
      {/* Yard lines */}
      {Array.from({ length: yardLineCount }).map((_, i) => {
        const x = (i - (yardLineCount - 1) / 2) * ((length - 2 * endZoneDepth) / yardLineCount);
        return (
          <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.032, 0]}>
            <planeGeometry args={[0.14, width - 0.6]} />
            <meshBasicMaterial color={colors.line} />
          </mesh>
        );
      })}
      {/* Midfield line — thicker */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.033, 0]}>
        <planeGeometry args={[0.28, width - 0.4]} />
        <meshBasicMaterial color={colors.line} />
      </mesh>
      {/* Goal posts on each end */}
      {[-1, 1].map((sign) => (
        <group key={sign} position={[sign * (length / 2 - 0.1), 0, 0]}>
          {/* Pole */}
          <mesh castShadow position={[0, 1.7, 0]}>
            <cylinderGeometry args={[0.07, 0.09, 3.4, 8]} />
            <meshStandardMaterial color="#eab308" roughness={0.4} emissive="#eab308" emissiveIntensity={0.2} />
          </mesh>
          {/* Crossbar */}
          <mesh castShadow position={[0, 2.7, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.07, 0.07, 3.6, 8]} />
            <meshStandardMaterial color="#eab308" roughness={0.4} emissive="#eab308" emissiveIntensity={0.2} />
          </mesh>
          {/* Uprights */}
          {[-1, 1].map((sz) => (
            <mesh key={sz} castShadow position={[0, 3.9, sz * 1.8]}>
              <cylinderGeometry args={[0.07, 0.07, 2.4, 8]} />
              <meshStandardMaterial color="#eab308" roughness={0.4} emissive="#eab308" emissiveIntensity={0.2} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

// ---------------------------------------------------------------------------
// BaseballDiamond — the west-wing Home Run Derby field, relocated and scaled
// up (~1.7×) so it no longer clips the west-wing buildings.
//
// Layout, from east (closest to the entrance plaza) to west (deep outfield):
//   • V-shape bleachers behind home plate (east side, on the quad-facing edge)
//   • Home plate + batter's box (batter faces −X, i.e. toward the outfield)
//   • Rotated dirt diamond infield with 4 white bases
//   • Pitcher's mound at the centre of the diamond
//   • Second base at the far infield corner
//   • Semi-circular arced outfield in green
//   • Home-run wall as a curved arc at the outfield edge
// ---------------------------------------------------------------------------
function BaseballDiamond({ teamColor }) {
  const [ax, , az] = SPORT_CENTERS.baseball;
  const cfg = BASEBALL_DIAMOND;
  const { colors } = cfg;

  // Home plate sits +plateOffset east of the sport centre — closest to the
  // entrance plaza so the visitor's approach corridor lands at the batter.
  const plateX = ax + cfg.plateOffset;
  const diamondHalf = cfg.size / 2;

  // Bases relative to home plate. Batter faces −X so 1st base is on +Z from
  // the plate (batter's right), 3rd on −Z, 2nd directly west.
  const bases = [
    { key: "home",  x: plateX,               z: az,               size: 1.1, color: colors.homePlate },
    { key: "first", x: plateX - diamondHalf, z: az + diamondHalf, size: 1.1, color: colors.base },
    { key: "second",x: plateX - cfg.size,    z: az,               size: 1.1, color: colors.base },
    { key: "third", x: plateX - diamondHalf, z: az - diamondHalf, size: 1.1, color: colors.base },
  ];

  return (
    <group>
      {/* Semi-circular OUTFIELD — a half-disc centred on home plate opening
          to the west. `thetaStart = Math.PI/2` + `thetaLength = Math.PI`
          gives us the +Z through −X through −Z half, i.e. everything west
          of home plate. */}
      <mesh
        receiveShadow
        rotation={[-Math.PI / 2, 0, 0]}
        position={[plateX, 0.02, az]}
      >
        <circleGeometry args={[cfg.outfieldRadius, 48, Math.PI / 2, Math.PI]} />
        <meshStandardMaterial color={colors.grass} roughness={0.85} />
      </mesh>
      {/* Alternating radial mow-stripes for that stadium look */}
      {Array.from({ length: 7 }, (_, i) => {
        const a0 = Math.PI / 2 + (i / 7) * Math.PI;
        const a1 = a0 + (Math.PI / 7) * 0.5;
        return (
          <mesh
            key={i}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[plateX, 0.023, az]}
          >
            <ringGeometry args={[6, cfg.outfieldRadius, 20, 1, a0, a1 - a0]} />
            <meshStandardMaterial color={colors.grassAlt} transparent opacity={0.28} />
          </mesh>
        );
      })}

      {/* Infield DIRT — a rotated square from home plate to 2nd base, with a
          circle at the pitcher's mound and dirt paths connecting bases */}
      <mesh
        receiveShadow
        rotation={[-Math.PI / 2, 0, Math.PI / 4]}
        position={[plateX - diamondHalf, 0.03, az]}
      >
        <planeGeometry args={[cfg.size, cfg.size]} />
        <meshStandardMaterial color={colors.dirt} roughness={0.9} />
      </mesh>
      {/* Circular grass patch inside the infield (baseball tradition) */}
      <mesh
        receiveShadow
        rotation={[-Math.PI / 2, 0, 0]}
        position={[plateX - diamondHalf, 0.04, az]}
      >
        <circleGeometry args={[cfg.size / 3.2, 32]} />
        <meshStandardMaterial color={colors.grass} roughness={0.85} />
      </mesh>

      {/* Foul lines — from home plate to the outfield along the ±Z diagonals */}
      {[1, -1].map((s) => {
        const dx = -cfg.outfieldRadius / Math.SQRT2;
        const dz = s * cfg.outfieldRadius / Math.SQRT2;
        const midX = plateX + dx / 2;
        const midZ = az + dz / 2;
        const len = Math.sqrt(dx * dx + dz * dz);
        const angle = Math.atan2(dz, dx);
        return (
          <mesh
            key={s}
            rotation={[-Math.PI / 2, 0, -angle]}
            position={[midX, 0.05, midZ]}
          >
            <planeGeometry args={[len, 0.18]} />
            <meshBasicMaterial color={colors.line} />
          </mesh>
        );
      })}

      {/* Pitcher's mound — a raised disc at the centre of the diamond */}
      <mesh
        castShadow
        receiveShadow
        position={[plateX - diamondHalf, 0.18, az]}
      >
        <cylinderGeometry args={[2.2, 2.5, 0.36, 24]} />
        <meshStandardMaterial color={colors.mound} roughness={0.85} />
      </mesh>
      {/* Pitcher's rubber */}
      <mesh position={[plateX - diamondHalf, 0.37, az]}>
        <boxGeometry args={[0.8, 0.06, 0.2]} />
        <meshBasicMaterial color={colors.line} />
      </mesh>

      {/* Bases + home plate */}
      {bases.map((b) => (
        <mesh
          key={b.key}
          castShadow
          position={[b.x, 0.08, b.z]}
          rotation={b.key === "home" ? [0, Math.PI / 4, 0] : [0, 0, 0]}
        >
          <boxGeometry args={[b.size, 0.12, b.size]} />
          <meshStandardMaterial color={b.color} roughness={0.5} />
        </mesh>
      ))}

      {/* Batter's box outline — team-coloured plane just off home plate,
          the on-ramp spot for the visitor's avatar. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[plateX + 0.7, 0.05, az]}>
        <planeGeometry args={[1.8, 2.4]} />
        <meshBasicMaterial color={teamColor || colors.line} transparent opacity={0.4} />
      </mesh>

      {/* HOME-RUN WALL — arced fence following the outfield radius */}
      {(() => {
        const N = 22;
        const arcStart = Math.PI / 2;     // +Z direction
        const arcSweep = Math.PI;         // sweeps through −X to −Z
        const r = cfg.homeRunDistance;
        const parts = [];
        for (let i = 0; i < N; i++) {
          const t = i / (N - 1);
          const a = arcStart + t * arcSweep;
          const wx = plateX + Math.cos(a) * r;
          const wz = az + Math.sin(a) * r;
          // Tangent angle so segment aligns with the wall arc.
          const tangent = a + Math.PI / 2;
          const segLen = (r * arcSweep) / N;
          parts.push(
            <group key={i} position={[wx, 0, wz]} rotation={[0, -tangent, 0]}>
              <mesh castShadow position={[0, 1.3, 0]}>
                <boxGeometry args={[segLen, 2.6, 0.32]} />
                <meshStandardMaterial color={colors.fence} />
              </mesh>
              <mesh position={[0, 2.65, 0]}>
                <boxGeometry args={[segLen + 0.15, 0.16, 0.44]} />
                <meshStandardMaterial
                  color={colors.fenceTop}
                  emissive={colors.fenceTop}
                  emissiveIntensity={0.35}
                />
              </mesh>
            </group>
          );
        }
        return parts;
      })()}

      {/* V-SHAPE BLEACHERS behind home plate — two straight stands angled at
          ±35° opening east so they face the batter and the outfield.
          Each stand has 4 stepped rows of blocky spectators. */}
      {(() => {
        const stands = [+1, -1].map((side) => {
          const angle = side * (Math.PI / 5); // ~36° from +X axis
          const distAnchor = 3.2;             // start distance from home plate
          const standLen = 14;
          const anchorX = plateX + Math.cos(angle) * distAnchor;
          const anchorZ = az + Math.sin(angle) * distAnchor;
          // The stand extends outward along its own angle direction
          return { anchorX, anchorZ, angle, standLen, side };
        });
        return stands.map((s, i) => (
          <group
            key={i}
            position={[s.anchorX, 0, s.anchorZ]}
            rotation={[0, -s.angle - Math.PI / 2, 0]}
          >
            {/* Four stepped rows of bleacher benches */}
            {Array.from({ length: 4 }, (_, row) => (
              <mesh
                key={row}
                castShadow
                receiveShadow
                position={[0, 0.25 + row * 0.4, row * 0.7]}
              >
                <boxGeometry args={[s.standLen, 0.32, 0.7]} />
                <meshStandardMaterial
                  color={row % 2 === 0 ? "#1e293b" : "#334155"}
                  roughness={0.7}
                />
              </mesh>
            ))}
            {/* Instanced blocky spectators along each row — 10 per row */}
            {Array.from({ length: 4 }, (_, row) =>
              Array.from({ length: 10 }, (_, col) => {
                const x = -s.standLen / 2 + 0.75 + col * (s.standLen - 1.5) / 9;
                const y = 0.25 + row * 0.4 + 0.55;
                const z = row * 0.7;
                // Deterministic jersey colour per (row,col)
                const jerseys = ["#c8353a", "#e6b450", "#4bb352", "#3b7ee6", "#7d4dd6", "#e78429"];
                const col2 = jerseys[(row * 10 + col) % jerseys.length];
                return (
                  <group key={`${row}-${col}`} position={[x, y, z]}>
                    <mesh castShadow>
                      <boxGeometry args={[0.35, 0.5, 0.35]} />
                      <meshStandardMaterial color={col2} />
                    </mesh>
                    <mesh position={[0, 0.42, 0]}>
                      <boxGeometry args={[0.24, 0.24, 0.24]} />
                      <meshStandardMaterial color="#e8b48a" />
                    </mesh>
                  </group>
                );
              })
            )}
          </group>
        ));
      })()}
    </group>
  );
}

// ---------------------------------------------------------------------------
// PitcherNPC — a blocky Roblox-style figure on the mound. Arm rotates
// forward on every pitch (windup/release cycle) driven by `phaseRef`, a
// 0..1 value written by the parent's per-frame pitch state machine.
// ---------------------------------------------------------------------------
function PitcherNPC({ phaseRef }) {
  const [ax, , az] = SPORT_CENTERS.baseball;
  const cfg = BASEBALL_DIAMOND;
  // Pitcher now stands on the CENTRAL mound (at the middle of the diamond),
  // one diamondHalf west of home plate.
  const plateX = ax + cfg.plateOffset;
  const mx = plateX - cfg.size / 2;
  const mz = az;
  const armRef = useRef();
  useFrame(() => {
    if (!armRef.current) return;
    const p = phaseRef.current || 0;
    // Windup 0-0.4: arm rotates back (+Z axis); release 0.4-0.7: whips
    // forward to -Z; follow-through 0.7-1: settles back.
    let angle;
    if (p < 0.4) angle = -0.6 * (p / 0.4);
    else if (p < 0.7) angle = -0.6 + 2.6 * ((p - 0.4) / 0.3);
    else angle = 2.0 * (1 - (p - 0.7) / 0.3);
    armRef.current.rotation.x = angle;
  });
  return (
    <group position={[mx, 0.36, mz]} rotation={[0, -Math.PI / 2, 0]}>
      {/* Legs */}
      <mesh castShadow position={[-0.18, 0.5, 0]}>
        <boxGeometry args={[0.28, 1.0, 0.28]} />
        <meshStandardMaterial color="#2a2f42" />
      </mesh>
      <mesh castShadow position={[0.18, 0.5, 0]}>
        <boxGeometry args={[0.28, 1.0, 0.28]} />
        <meshStandardMaterial color="#2a2f42" />
      </mesh>
      {/* Torso — jersey */}
      <mesh castShadow position={[0, 1.35, 0]}>
        <boxGeometry args={[0.75, 0.7, 0.4]} />
        <meshStandardMaterial color="#c8353a" />
      </mesh>
      {/* Head */}
      <mesh castShadow position={[0, 1.98, 0]}>
        <boxGeometry args={[0.44, 0.44, 0.44]} />
        <meshStandardMaterial color="#e8b48a" />
      </mesh>
      {/* Cap */}
      <mesh position={[0, 2.25, 0]}>
        <boxGeometry args={[0.5, 0.14, 0.5]} />
        <meshStandardMaterial color="#0b1530" />
      </mesh>
      <mesh position={[0, 2.19, 0.28]}>
        <boxGeometry args={[0.5, 0.06, 0.18]} />
        <meshStandardMaterial color="#0b1530" />
      </mesh>
      {/* Non-throwing arm */}
      <mesh castShadow position={[-0.5, 1.35, 0]}>
        <boxGeometry args={[0.22, 0.7, 0.22]} />
        <meshStandardMaterial color="#c8353a" />
      </mesh>
      {/* Throwing arm — pivots around shoulder */}
      <group position={[0.5, 1.65, 0]} ref={armRef}>
        <mesh castShadow position={[0, -0.35, 0]}>
          <boxGeometry args={[0.22, 0.7, 0.22]} />
          <meshStandardMaterial color="#c8353a" />
        </mesh>
        {/* Ball in hand — hidden most of the time */}
      </group>
    </group>
  );
}

// ---------------------------------------------------------------------------
// BaseballBatter — a Roblox-style figure at home plate holding a bat. Bat
// rotates on every swing driven by `swingPhaseRef` (0..1).
// ---------------------------------------------------------------------------
function BaseballBatter({ swingPhaseRef, teamColor }) {
  const [ax, , az] = SPORT_CENTERS.baseball;
  const cfg = BASEBALL_DIAMOND;
  const px = ax + cfg.plateOffset + 0.8;
  const pz = az + 0.5;
  const batRef = useRef();
  useFrame(() => {
    if (!batRef.current) return;
    const p = swingPhaseRef.current || 0;
    // Rest at rotationY=0 (bat over shoulder), rotate through -Math.PI/1.4
    // during the swing, then settle back.
    let ang;
    if (p < 0.2) ang = 0;
    else if (p < 0.55) ang = -Math.PI * 1.2 * ((p - 0.2) / 0.35);
    else if (p < 1.0) ang = -Math.PI * 1.2 * (1 - (p - 0.55) / 0.45);
    else ang = 0;
    batRef.current.rotation.y = ang;
  });
  return (
    <group position={[px, 0.28, pz]}>
      {/* Legs */}
      <mesh castShadow position={[-0.18, 0.5, 0]}>
        <boxGeometry args={[0.28, 1.0, 0.28]} />
        <meshStandardMaterial color="#2a2f42" />
      </mesh>
      <mesh castShadow position={[0.18, 0.5, 0]}>
        <boxGeometry args={[0.28, 1.0, 0.28]} />
        <meshStandardMaterial color="#2a2f42" />
      </mesh>
      {/* Torso */}
      <mesh castShadow position={[0, 1.35, 0]}>
        <boxGeometry args={[0.75, 0.7, 0.4]} />
        <meshStandardMaterial color={teamColor || "#1d4bd8"} />
      </mesh>
      {/* Head */}
      <mesh castShadow position={[0, 1.98, 0]}>
        <boxGeometry args={[0.44, 0.44, 0.44]} />
        <meshStandardMaterial color="#e8b48a" />
      </mesh>
      {/* Batting helmet */}
      <mesh position={[0, 2.25, 0]}>
        <boxGeometry args={[0.5, 0.24, 0.5]} />
        <meshStandardMaterial color={teamColor || "#1d4bd8"} />
      </mesh>
      {/* Front arm */}
      <mesh castShadow position={[-0.5, 1.35, 0]}>
        <boxGeometry args={[0.22, 0.7, 0.22]} />
        <meshStandardMaterial color={teamColor || "#1d4bd8"} />
      </mesh>
      {/* Back arm + bat, pivoting around the shoulder */}
      <group position={[0.42, 1.7, 0]} ref={batRef}>
        <mesh castShadow position={[0.35, 0.05, 0]}>
          <boxGeometry args={[0.6, 0.16, 0.16]} />
          <meshStandardMaterial color={teamColor || "#1d4bd8"} />
        </mesh>
        {/* Bat — brown cylinder */}
        <mesh castShadow position={[0.9, 0.1, 0]} rotation={[0, 0, -Math.PI / 2]}>
          <cylinderGeometry args={[0.08, 0.05, 1.1, 10]} />
          <meshStandardMaterial color="#6b3f24" roughness={0.7} />
        </mesh>
      </group>
    </group>
  );
}

// ---------------------------------------------------------------------------
// HomeRunSign — a giant floating "HR: X · OUT: Y · Pitches: Z / 10" pill
// hanging behind the home-run wall so it reads from the batter's box.
// ---------------------------------------------------------------------------
function HomeRunSign({ playerScore, cpuScore, pitchCount, totalPitches }) {
  const [ax, , az] = SPORT_CENTERS.baseball;
  const cfg = BASEBALL_DIAMOND;
  const plateX = ax + cfg.plateOffset;
  // Directly behind 2nd base at HR-wall distance, hoisted 6m so it reads
  // over the fence from the batter's perspective.
  const signX = plateX - cfg.homeRunDistance - 2.5;
  return (
    <group position={[signX, 6.5, az]} rotation={[0, Math.PI / 2, 0]}>
      <mesh>
        <boxGeometry args={[0.4, 2.2, 9.0]} />
        <meshStandardMaterial
          color="#0b1530"
          emissive="#e6b450"
          emissiveIntensity={0.15}
        />
      </mesh>
      <mesh position={[-0.22, 0, 0]}>
        <boxGeometry args={[0.04, 2.35, 9.2]} />
        <meshBasicMaterial color="#e6b450" />
      </mesh>
      <Html
        position={[-0.3, 0, 0]}
        transform
        rotation={[0, -Math.PI / 2, 0]}
        distanceFactor={11}
        style={{ pointerEvents: "none", userSelect: "none" }}
      >
        <div
          style={{
            width: 420,
            textAlign: "center",
            color: "#f7dc95",
            fontFamily: "'Cormorant Garamond','Playfair Display',serif",
            fontWeight: 700,
            letterSpacing: 3,
          }}
        >
          <div style={{ fontSize: 22, opacity: 0.85 }}>HOME RUN DERBY</div>
          <div style={{ fontSize: 66, lineHeight: 1, margin: "8px 0" }}>
            HR {playerScore} · OUT {cpuScore}
          </div>
          <div style={{ fontSize: 18, opacity: 0.75 }}>
            PITCH {pitchCount} / {totalPitches}
          </div>
        </div>
      </Html>
    </group>
  );
}



// ---------------------------------------------------------------------------
// Roblox-style opponent NPC (single blocky character with chase-and-shoot).
// ---------------------------------------------------------------------------
function OpponentNPC({
  index,
  jerseyColor,
  positionRef,       // THREE.Vector3 owned by parent — updated by AI tick
}) {
  const rootRef = useRef();
  const legLeftRef = useRef();
  const legRightRef = useRef();
  const armLeftRef = useRef();
  const armRightRef = useRef();
  const t0 = useRef(Math.random() * 3);

  useFrame((state) => {
    const g = rootRef.current;
    if (!g) return;
    g.position.copy(positionRef.current);

    // Walk cycle amplitude scales with the horizontal speed magnitude,
    // which we approximate by frame-to-frame delta on the position ref.
    const t = state.clock.elapsedTime + t0.current;
    const swing = Math.sin(t * 7) * 0.6;
    if (legLeftRef.current) legLeftRef.current.rotation.x = swing;
    if (legRightRef.current) legRightRef.current.rotation.x = -swing;
    if (armLeftRef.current) armLeftRef.current.rotation.x = -swing * 0.9;
    if (armRightRef.current) armRightRef.current.rotation.x = swing * 0.9;

    // Face the ball: rotate root toward the AI's facing vector, which the
    // parent stashes on positionRef as `.facing` (radians).
    if (typeof positionRef.current.facing === "number") {
      g.rotation.y = positionRef.current.facing;
    }
  });

  return (
    <group
      ref={rootRef}
      // small chunky scale — reads as Roblox at ~1.7m total height
    >
      {/* Torso */}
      <mesh castShadow position={[0, 0.9, 0]}>
        <boxGeometry args={[0.7, 0.9, 0.4]} />
        <meshStandardMaterial color={jerseyColor} roughness={0.65} />
      </mesh>
      {/* Head */}
      <mesh castShadow position={[0, 1.65, 0]}>
        <boxGeometry args={[0.55, 0.55, 0.55]} />
        <meshStandardMaterial color="#f5d6b3" roughness={0.7} />
      </mesh>
      {/* Eyes — tiny black cubes */}
      <mesh position={[0.12, 1.72, 0.28]}>
        <boxGeometry args={[0.07, 0.07, 0.03]} />
        <meshBasicMaterial color="#111827" />
      </mesh>
      <mesh position={[-0.12, 1.72, 0.28]}>
        <boxGeometry args={[0.07, 0.07, 0.03]} />
        <meshBasicMaterial color="#111827" />
      </mesh>
      {/* Arms */}
      <group ref={armLeftRef} position={[-0.5, 1.32, 0]}>
        <mesh castShadow position={[0, -0.35, 0]}>
          <boxGeometry args={[0.25, 0.75, 0.25]} />
          <meshStandardMaterial color={jerseyColor} roughness={0.65} />
        </mesh>
      </group>
      <group ref={armRightRef} position={[0.5, 1.32, 0]}>
        <mesh castShadow position={[0, -0.35, 0]}>
          <boxGeometry args={[0.25, 0.75, 0.25]} />
          <meshStandardMaterial color={jerseyColor} roughness={0.65} />
        </mesh>
      </group>
      {/* Legs */}
      <group ref={legLeftRef} position={[-0.18, 0.44, 0]}>
        <mesh castShadow position={[0, -0.42, 0]}>
          <boxGeometry args={[0.26, 0.85, 0.28]} />
          <meshStandardMaterial color="#1e293b" roughness={0.7} />
        </mesh>
      </group>
      <group ref={legRightRef} position={[0.18, 0.44, 0]}>
        <mesh castShadow position={[0, -0.42, 0]}>
          <boxGeometry args={[0.26, 0.85, 0.28]} />
          <meshStandardMaterial color="#1e293b" roughness={0.7} />
        </mesh>
      </group>
    </group>
  );
}

// ---------------------------------------------------------------------------
// AI ticker — chases ball, tries to shoot toward PLAYER goal.
// ---------------------------------------------------------------------------
function useOpponentAI({
  npcPositions,          // array of THREE.Vector3 refs (with .facing prop)
  ready,
  sport,
  difficulty,
  getBallPosition,
  kickBallFromNpc,
  controlledIndexRef,
}) {
  // Per-NPC transient state (last shot time, reaction delay start, etc.).
  const stateRef = useRef(
    Array.from({ length: 13 }, () => ({
      lastShotAt: 0,
      spawnAt: performance.now(),
    }))
  );

  useEffect(() => {
    // Reset reaction clock whenever difficulty or sport changes.
    stateRef.current.forEach((s) => {
      s.spawnAt = performance.now();
      s.lastShotAt = 0;
    });
  }, [sport, difficulty]);

  useFrame((_, dt) => {
    if (!ready) return;
    // Baseball uses a different game loop (pitch/swing state machine in
    // SportsArena) — no chase-and-shoot NPCs.
    if (sport === "baseball") return;
    const cfg = DIFFICULTIES[difficulty];
    const sportCfg = SPORTS[sport];
    const ballPos = getBallPosition();
    const now = performance.now();
    const [ax, , az] = ARENA_CENTER;

    for (let i = 0; i < npcPositions.length; i++) {
      const pos = npcPositions[i].current;
      const st = stateRef.current[i];
      if (!pos) continue;
      // Skip the slot currently controlled by the visitor (WR/RB/DB roles).
      if (controlledIndexRef && controlledIndexRef.current === i) continue;

      // Reaction hold-off after spawn / ball-reset.
      if (now - st.spawnAt < cfg.reactionDelayMs) continue;

      // -----------------------------------------------------------------
      // FOOTBALL: role-based AI
      //   - teammates run their assigned routes (slant/go/block) — they
      //     don't try to touch the ball; the throw handler snaps them to
      //     it when a catch check succeeds.
      //   - defenders blitz toward the ball (either the pre-snap spot,
      //     the QB, or the loose football in flight).
      // -----------------------------------------------------------------
      if (sport === "football") {
        if (pos.role === "teammate") {
          // Simple routes: run toward the CPU end zone (-X direction).
          const targetX = ax - 8; // deep into CPU end zone
          let targetZ = pos.startPos[2];
          if (pos.route === "slant") {
            // Slant: angle toward the middle of the field as they run.
            const midBias = pos.startPos[2] > az ? -0.7 : 0.7;
            targetZ = pos.startPos[2] + midBias * 3;
          } else if (pos.route === "block") {
            // Blockers hang back near the line to protect the QB.
            const bx = pos.startPos[0] - 0.5;
            const dx0 = bx - pos.x;
            const dz0 = pos.startPos[2] - pos.z;
            const d = Math.hypot(dx0, dz0);
            if (d > 0.05) {
              const s = Math.min(cfg.speed * 0.6 * dt, d);
              pos.x += (dx0 / d) * s;
              pos.z += (dz0 / d) * s;
            }
            pos.facing = Math.PI; // facing toward defenders
            continue;
          }
          // go / slant: run toward the target point at ~half the base speed.
          const dxT = targetX - pos.x;
          const dzT = targetZ - pos.z;
          const d = Math.hypot(dxT, dzT);
          if (d > 0.05) {
            const s = Math.min((cfg.speed * 0.9) * dt, d);
            pos.x += (dxT / d) * s;
            pos.z += (dzT / d) * s;
            pos.facing = Math.atan2(dxT, dzT);
          }
          continue;
        }
        // Defender: chase the ball (which is initially at centre, then
        // moves when thrown). Slightly higher speed than the offense.
        const dxB = ballPos.x - pos.x;
        const dzB = ballPos.z - pos.z;
        const dB = Math.hypot(dxB, dzB);
        pos.facing = Math.atan2(dxB, dzB);
        if (dB > 0.3) {
          const s = Math.min(cfg.speed * 1.1 * dt, dB);
          pos.x += (dxB / dB) * s;
          pos.z += (dzB / dB) * s;
        }
        continue;
      }

      // -----------------------------------------------------------------
      // BASKETBALL / SOCCER: original chase-and-shoot AI
      // -----------------------------------------------------------------
      const dx = ballPos.x - pos.x;
      const dz = ballPos.z - pos.z;
      const dist = Math.hypot(dx, dz);
      pos.facing = Math.atan2(dx, dz);

      if (dist > NPC_KICK_RADIUS) {
        const step = Math.min(cfg.speed * dt, dist);
        const inv = 1 / dist;
        pos.x += dx * inv * step;
        pos.z += dz * inv * step;
      } else if (now - st.lastShotAt > cfg.cooldownMs) {
        st.lastShotAt = now;
        const isClean = Math.random() < cfg.successRate;
        const jitter = isClean ? 0.0 : cfg.aimJitter;
        const impulseScale = 0.85 + (cfg.speed - 1.8) * 0.06;
        kickBallFromNpc(
          new THREE.Vector3(pos.x, pos.y, pos.z),
          sportCfg.playerGoal.center,
          impulseScale,
          jitter
        );
      }
    }
  });
}

// ---------------------------------------------------------------------------
// EntrancePlaza — a small circular plaza with a gold "SPORTS" arch that
// sits at ENTRANCE_CENTER and marks the hub the three sport fields fan
// out from. It's the fixed teleport target for the HUD's "Go to Arena"
// button and gives visitors a clear arrival landmark whether they walk in
// from the quad or teleport in from the HUD.
// ---------------------------------------------------------------------------
function EntrancePlaza() {
  const [ex, , ez] = ENTRANCE_CENTER;
  const beamH = 4.6;
  const armW = 0.4;
  const spanX = 6.2;
  return (
    <group position={[ex, 0, ez]}>
      {/* Paved plaza — hex disc so it reads as a hub, not another field */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]}>
        <circleGeometry args={[4.0, 6]} />
        <meshStandardMaterial color="#c9b382" roughness={0.85} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.04, 0]}>
        <ringGeometry args={[3.75, 4.0, 6]} />
        <meshBasicMaterial color="#e6b450" />
      </mesh>

      {/* Two pillars + a crossbeam forming a stylised entrance arch */}
      {[-1, 1].map((s) => (
        <mesh key={s} castShadow position={[s * (spanX / 2), beamH / 2, 0]}>
          <boxGeometry args={[armW, beamH, armW]} />
          <meshStandardMaterial color="#0b1530" roughness={0.6} />
        </mesh>
      ))}
      <mesh castShadow position={[0, beamH + 0.1, 0]}>
        <boxGeometry args={[spanX + armW, 0.5, armW * 1.4]} />
        <meshStandardMaterial
          color="#e6b450"
          emissive="#e6b450"
          emissiveIntensity={0.45}
          roughness={0.4}
        />
      </mesh>
      {/* Small gold banner puck under the beam */}
      <mesh position={[0, beamH - 0.35, armW / 2 + 0.02]}>
        <boxGeometry args={[3.0, 0.5, 0.06]} />
        <meshStandardMaterial
          color="#0b1530"
          emissive="#e6b450"
          emissiveIntensity={0.3}
        />
      </mesh>
    </group>
  );
}

// ---------------------------------------------------------------------------
// SportPathways — three gold chevron trails painted on the ground from the
// entrance plaza to each of the sport fields. Purely visual wayfinding: no
// interaction, no physics, no state. Each pathway is ~7 shallow triangles
// laid flat and pointed toward the destination sport, spaced evenly along
// the straight line from ENTRANCE_CENTER to that sport's centre. The chevron
// closest to the destination is drawn at ~70% opacity of the first so the
// "trail" reads directionally without a label.
// ---------------------------------------------------------------------------
function SportPathways() {
  const [ex, , ez] = ENTRANCE_CENTER;
  // Small chevron shape: an isoceles triangle 1.4m long × 1.1m wide, apex
  // pointing "forward" along +X in local space. We rotate the whole group
  // per-pathway to aim toward each destination.
  const chevron = useMemo(() => {
    const shape = new THREE.Shape();
    shape.moveTo(0.7, 0);
    shape.lineTo(-0.7, 0.55);
    shape.lineTo(-0.35, 0);
    shape.lineTo(-0.7, -0.55);
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, []);

  const pathways = useMemo(() => {
    // Each entry: [x, z, angleY] for the target sport's centre in world
    // space. Angle is the rotation-Y of the chevron group so the apex
    // points from the plaza toward the field.
    const targets = [
      { center: SPORT_CENTERS.basketball, label: "basketball" },
      { center: SPORT_CENTERS.football,   label: "football" },
      { center: SPORT_CENTERS.soccer,     label: "soccer" },
      { center: SPORT_CENTERS.baseball,   label: "baseball" },
    ];
    return targets.map((t) => {
      const [tx, , tz] = t.center;
      const dx = tx - ex;
      const dz = tz - ez;
      const dist = Math.sqrt(dx * dx + dz * dz);
      // Aim: chevron local +X should point toward (dx, dz). In world coords
      // that's atan2(dz, dx) rotated around +Y (three.js Y-up), and because
      // the chevron is laid flat with rotation.x = -Math.PI/2, we need to
      // negate the Z contribution to get the on-plane orientation right.
      const angleY = -Math.atan2(dz, dx);
      return { center: t.center, dx, dz, dist, angleY, label: t.label };
    });
  }, [ex, ez]);

  // Number of chevrons per pathway; skip the ones inside the plaza (r=4)
  // and inside the field (~half of the smallest court dim ~= 5.5m).
  const N = 7;
  const startOffset = 4.6;    // clear of the plaza's 4m disc
  const endOffset = 6.0;      // stop short of the field so it doesn't overlap

  return (
    <group>
      {pathways.map((p, pi) => {
        const usable = Math.max(0, p.dist - startOffset - endOffset);
        // Unit vector from plaza to target.
        const ux = p.dx / p.dist;
        const uz = p.dz / p.dist;
        return (
          <group key={p.label}>
            {Array.from({ length: N }, (_, i) => {
              const t = N === 1 ? 0.5 : i / (N - 1);
              const s = startOffset + t * usable;
              const x = ex + ux * s;
              const z = ez + uz * s;
              // Fade the trailing chevrons slightly so the visitor's eye
              // is pulled toward the destination (last one is brightest).
              const alpha = 0.55 + 0.35 * t;
              return (
                <mesh
                  key={i}
                  geometry={chevron}
                  position={[x, 0.06, z]}
                  rotation={[-Math.PI / 2, 0, p.angleY]}
                  renderOrder={2}
                >
                  <meshBasicMaterial
                    color="#e6b450"
                    transparent
                    opacity={alpha}
                    depthWrite={false}
                    polygonOffset
                    polygonOffsetFactor={-1}
                    polygonOffsetUnits={-1}
                  />
                </mesh>
              );
            })}
          </group>
        );
      })}
    </group>
  );
}


// ---------------------------------------------------------------------------
// SportSignageBanners — a tall Roblox-style banner at the terminus of each
// pathway. Each banner shows the sport's name + a colour-coded icon puck
// so a first-time visitor can spot the destination from anywhere on the
// quad. Purely visual — no interactions.

// ---------------------------------------------------------------------------
// SportSignposts — small blocky wooden signposts placed at the plaza
// intersection, each pointing along its pathway toward a sport. Reads like
// a fingerpost trail sign — a pole with 4 arrow-shaped boards, each aimed
// at its destination and labelled with the sport name.
// ---------------------------------------------------------------------------
function SportSignposts() {
  const [ex, , ez] = ENTRANCE_CENTER;

  const signs = useMemo(() => {
    const meta = [
      { sport: "basketball", label: "BASKETBALL", color: "#f26a34" },
      { sport: "football",   label: "FOOTBALL",   color: "#7d4dd6" },
      { sport: "soccer",     label: "SOCCER",     color: "#4bb352" },
      { sport: "baseball",   label: "BASEBALL",   color: "#c8353a" },
    ];
    return meta.map((m, i) => {
      const [tx, , tz] = SPORT_CENTERS[m.sport];
      const angleY = -Math.atan2(tz - ez, tx - ex);
      // Stack boards vertically on the pole so they don't overlap
      return { ...m, angleY, y: 2.9 - i * 0.55 };
    });
  }, [ex, ez]);

  return (
    <group position={[ex + 4.6, 0, ez + 0]}>
      {/* Signpost pole — dark wood colour */}
      <mesh castShadow position={[0, 1.85, 0]}>
        <cylinderGeometry args={[0.09, 0.11, 3.7, 10]} />
        <meshStandardMaterial color="#3a2513" roughness={0.85} />
      </mesh>
      {/* Small cap on top */}
      <mesh position={[0, 3.75, 0]}>
        <boxGeometry args={[0.28, 0.18, 0.28]} />
        <meshStandardMaterial color="#e6b450" emissive="#e6b450" emissiveIntensity={0.3} />
      </mesh>

      {/* Four fingerpost boards, one per sport */}
      {signs.map((s) => (
        <group key={s.sport} position={[0, s.y, 0]} rotation={[0, s.angleY, 0]}>
          {/* Board — a pentagonal arrow shape approximated as a stretched box */}
          <mesh castShadow position={[0.7, 0, 0]}>
            <boxGeometry args={[1.3, 0.42, 0.08]} />
            <meshStandardMaterial
              color={s.color}
              emissive={s.color}
              emissiveIntensity={0.25}
              roughness={0.5}
            />
          </mesh>
          {/* Arrow tip — a small pointed cap on the far end */}
          <mesh position={[1.42, 0, 0]} rotation={[0, 0, Math.PI / 4]}>
            <boxGeometry args={[0.3, 0.3, 0.08]} />
            <meshStandardMaterial color={s.color} />
          </mesh>
          {/* Label */}
          <Html
            position={[0.7, 0.02, 0.045]}
            transform
            distanceFactor={5}
            style={{ pointerEvents: "none", userSelect: "none" }}
          >
            <div
              style={{
                width: 160,
                textAlign: "center",
                color: "#f7dc95",
                fontFamily: "'Cormorant Garamond','Playfair Display',serif",
                fontWeight: 700,
                fontSize: 26,
                letterSpacing: 3,
                lineHeight: 1,
              }}
            >
              {s.label}
            </div>
          </Html>
        </group>
      ))}
    </group>
  );
}


// ---------------------------------------------------------------------------
function SportSignageBanners() {
  const [ex, , ez] = ENTRANCE_CENTER;
  // Banners sit just inside each field, on the side facing the entrance
  // plaza, so they read from the approach corridor.
  const banners = useMemo(() => {
    const meta = [
      { sport: "basketball", label: "BASKETBALL", color: "#f26a34", icon: "◍" },
      { sport: "football",   label: "FOOTBALL",   color: "#7d4dd6", icon: "🏈" },
      { sport: "soccer",     label: "SOCCER",     color: "#4bb352", icon: "⚽" },
      { sport: "baseball",   label: "BASEBALL",   color: "#c8353a", icon: "⚾" },
    ];
    return meta.map((m) => {
      const [tx, , tz] = SPORT_CENTERS[m.sport];
      // Position the banner on the plaza-facing edge of each field, roughly
      // 5m out from the field centre toward the plaza.
      const dx = ex - tx;
      const dz = ez - tz;
      const dist = Math.sqrt(dx * dx + dz * dz);
      const nx = dx / dist;
      const nz = dz / dist;
      // Offset from field centre toward plaza — 6m in, which for our chosen
      // field sizes lands just outside the field's near edge.
      const px = tx + nx * 6;
      const pz = tz + nz * 6;
      // Banner faces the plaza — rotate so its front is toward (ex, ez).
      const angleY = Math.atan2(-nx, -nz);
      return { ...m, x: px, z: pz, angleY };
    });
  }, [ex, ez]);

  return (
    <group>
      {banners.map((b) => (
        <group key={b.sport} position={[b.x, 0, b.z]} rotation={[0, b.angleY, 0]}>
          {/* Pole */}
          <mesh castShadow position={[0, 2.5, 0]}>
            <cylinderGeometry args={[0.14, 0.14, 5.0, 10]} />
            <meshStandardMaterial color="#0b1530" roughness={0.6} />
          </mesh>
          {/* Banner panel — vertical rectangle, sport-coloured with gold frame */}
          <mesh castShadow position={[0, 4.2, 0.1]}>
            <boxGeometry args={[2.4, 3.2, 0.08]} />
            <meshStandardMaterial
              color={b.color}
              emissive={b.color}
              emissiveIntensity={0.25}
              roughness={0.5}
            />
          </mesh>
          {/* Gold border frame */}
          <mesh position={[0, 4.2, 0.14]}>
            <boxGeometry args={[2.5, 3.3, 0.04]} />
            <meshBasicMaterial color="#e6b450" />
          </mesh>
          {/* Inner panel to cover most of the gold — leaves a thin border */}
          <mesh position={[0, 4.2, 0.16]}>
            <boxGeometry args={[2.32, 3.12, 0.03]} />
            <meshBasicMaterial color={b.color} />
          </mesh>
          {/* Label — DOM text via drei Html so we don't hit the drei <Text>
              font-loading flake and it reads sharp at any zoom */}
          <Html
            position={[0, 4.2, 0.19]}
            transform
            distanceFactor={7}
            style={{ pointerEvents: "none", userSelect: "none" }}
          >
            <div
              style={{
                width: 240,
                textAlign: "center",
                color: "#f7dc95",
                fontFamily: "'Cormorant Garamond','Playfair Display',serif",
                fontWeight: 700,
              }}
            >
              <div style={{ fontSize: 68, lineHeight: 1, marginBottom: 6 }}>
                {b.icon}
              </div>
              <div style={{ fontSize: 34, letterSpacing: 5, lineHeight: 1 }}>
                {b.label}
              </div>
            </div>
          </Html>
          {/* Small top-cap for silhouette */}
          <mesh position={[0, 6.0, 0.1]}>
            <boxGeometry args={[2.7, 0.2, 0.14]} />
            <meshStandardMaterial color="#e6b450" emissive="#e6b450" emissiveIntensity={0.3} />
          </mesh>
        </group>
      ))}
    </group>
  );
}



// ---------------------------------------------------------------------------
// Goal flash — a coloured billboard slab that pops for ~1.4s after each goal.
// No text (kept text out of the 3D scene so screenshot rendering stays fast
// and font-subset fetches never block first paint).
// ---------------------------------------------------------------------------
function GoalFlash({ lastGoal, center }) {
  const ref = useRef();
  const visibleUntilRef = useRef(0);
  const colorRef = useRef(new THREE.Color("#facc15"));

  useEffect(() => {
    if (!lastGoal) return;
    visibleUntilRef.current = lastGoal.at + 1400;
    colorRef.current.set(lastGoal.who === "player" ? "#facc15" : "#ef4444");
    if (ref.current) {
      ref.current.material.color.copy(colorRef.current);
      ref.current.material.emissive.copy(colorRef.current);
    }
  }, [lastGoal]);

  useFrame((state) => {
    const g = ref.current;
    if (!g) return;
    const now = Date.now();
    const visible = now < visibleUntilRef.current;
    g.visible = visible;
    if (visible) {
      const t = (visibleUntilRef.current - now) / 1400;
      const s = 0.9 + (1 - t) * 0.2;
      g.scale.set(s, s, 1);
      g.rotation.y = Math.sin(state.clock.elapsedTime * 4) * 0.06;
      g.material.opacity = 0.35 + t * 0.5;
    }
  });

  const [ax, , az] = center || ARENA_CENTER;
  return (
    <mesh ref={ref} position={[ax, 6, az]} visible={false}>
      <boxGeometry args={[6, 1.6, 0.25]} />
      <meshStandardMaterial
        color="#facc15"
        emissive="#facc15"
        emissiveIntensity={0.9}
        transparent
        opacity={0.8}
        roughness={0.3}
      />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// Ground click catcher — big invisible plane over the arena to capture kicks.
// ---------------------------------------------------------------------------
function ArenaClickPlane({ onKick, center, sport }) {
  const [ax, , az] = center || ARENA_CENTER;
  // Size the click plane to the active sport's footprint (+ buffer).
  const w =
    sport === "football" ? FOOTBALL_FIELD.length + 4
    : sport === "soccer" ? SOCCER_PITCH.length + 4
    : BASKETBALL_COURT.length + 4;
  const d =
    sport === "football" ? FOOTBALL_FIELD.width + 6
    : sport === "soccer" ? SOCCER_PITCH.width + 6
    : BASKETBALL_COURT.width + 6;
  const { camera } = useThree();

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[ax, 0.04, az]}
      onPointerDown={(e) => {
        e.stopPropagation();
        // e.point is the world hit point on this plane. Compute XZ distance
        // from player (camera) to the point; if the player is anywhere on
        // the arena, the kick is armed. useArenaPhysics rejects kicks when
        // player is beyond PLAYER_KICK_RADIUS of the ball.
        onKick(e.point, camera.position);
      }}
    >
      <planeGeometry args={[w, d]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// Signage — a bright arcade-style panel with a coloured logo puck so visitors
// can locate the arena at a glance. Deliberately no runtime text (kept as
// simple geometry for performance + snapshot-friendly rendering).
// ---------------------------------------------------------------------------
function ArenaSign({ sport, center }) {
  const [ax, , az] = center || ARENA_CENTER;
  const pushBack =
    sport === "basketball"
      ? BASKETBALL_COURT.width / 2 + 1.4
      : sport === "football"
      ? FOOTBALL_FIELD.width / 2 + 1.4
      : SOCCER_PITCH.width / 2 + 1.4;
  const puckColor =
    sport === "basketball" ? "#e67c2b" : sport === "football" ? "#8b4a2b" : "#4bb352";
  return (
    <group position={[ax, 3.6, az - pushBack]}>
      {/* Board */}
      <mesh castShadow>
        <boxGeometry args={[7, 1.4, 0.2]} />
        <meshStandardMaterial color="#0b1530" roughness={0.55} />
      </mesh>
      {/* Gold trim */}
      <mesh position={[0, 0, 0.11]}>
        <boxGeometry args={[6.6, 1.0, 0.04]} />
        <meshStandardMaterial
          color="#e6b450"
          emissive="#e6b450"
          emissiveIntensity={0.35}
          roughness={0.4}
        />
      </mesh>
      {/* Sport puck (basketball orange or soccer green) */}
      <mesh position={[-2.5, 0, 0.16]}>
        <cylinderGeometry args={[0.35, 0.35, 0.06, 20]} />
        <meshStandardMaterial
          color={puckColor}
          emissive={puckColor}
          emissiveIntensity={0.4}
          roughness={0.5}
        />
      </mesh>
      {/* Two little indicator lines representing the label */}
      <mesh position={[0.4, 0.15, 0.16]}>
        <boxGeometry args={[3.6, 0.14, 0.03]} />
        <meshStandardMaterial color="#0b1530" roughness={0.6} />
      </mesh>
      <mesh position={[0.4, -0.15, 0.16]}>
        <boxGeometry args={[2.8, 0.14, 0.03]} />
        <meshStandardMaterial color="#0b1530" roughness={0.6} />
      </mesh>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Referee NPC — stationary blocky character in a black/white striped shirt,
// stands courtside on the +Z sideline. When a goal fires (either direction)
// he throws his arms up briefly.
// ---------------------------------------------------------------------------
function Referee({ lastGoal, center, sport }) {
  const armLeftRef = useRef();
  const armRightRef = useRef();
  const bodyRef = useRef();
  const armsUpUntilRef = useRef(0);

  useEffect(() => {
    if (!lastGoal) return;
    armsUpUntilRef.current = Date.now() + 1400;
  }, [lastGoal]);

  useFrame((state) => {
    const now = Date.now();
    const raise = now < armsUpUntilRef.current;
    const targetX = raise ? -Math.PI * 0.9 : 0; // arms fully up when raised
    // Smoothed lerp so the arms actually swing rather than snap.
    if (armLeftRef.current) {
      armLeftRef.current.rotation.x = THREE.MathUtils.lerp(
        armLeftRef.current.rotation.x,
        targetX,
        0.15
      );
    }
    if (armRightRef.current) {
      armRightRef.current.rotation.x = THREE.MathUtils.lerp(
        armRightRef.current.rotation.x,
        targetX,
        0.15
      );
    }
    // Subtle body sway when arms are up.
    if (bodyRef.current) {
      const sway = raise ? Math.sin(state.clock.elapsedTime * 8) * 0.08 : 0;
      bodyRef.current.rotation.z = sway;
    }
  });

  const [ax, , az] = center || ARENA_CENTER;
  // Stand on the far sideline (positive Z), roughly at halfway.
  const sideWidth =
    sport === "football" ? FOOTBALL_FIELD.width
    : sport === "soccer" ? SOCCER_PITCH.width
    : BASKETBALL_COURT.width;
  const zSide = az + sideWidth / 2 + 1.2;

  return (
    <group position={[ax, 0, zSide]} rotation={[0, Math.PI, 0]}>
      <group ref={bodyRef}>
        {/* Torso — black/white stripes suggested via two stacked slim boxes */}
        <mesh castShadow position={[0, 0.9, 0]}>
          <boxGeometry args={[0.7, 0.9, 0.4]} />
          <meshStandardMaterial color="#ffffff" roughness={0.6} />
        </mesh>
        {[0.32, 0.52, 0.72, 0.92, 1.12].map((y, i) => (
          <mesh key={i} position={[0, y + 0.13, 0.202]}>
            <boxGeometry args={[0.72, 0.11, 0.005]} />
            <meshStandardMaterial color="#0b1530" roughness={0.6} />
          </mesh>
        ))}
        {/* Head */}
        <mesh castShadow position={[0, 1.65, 0]}>
          <boxGeometry args={[0.55, 0.55, 0.55]} />
          <meshStandardMaterial color="#f5d6b3" roughness={0.7} />
        </mesh>
        {/* Cap */}
        <mesh position={[0, 1.98, 0]}>
          <boxGeometry args={[0.62, 0.14, 0.62]} />
          <meshStandardMaterial color="#0b1530" roughness={0.5} />
        </mesh>
        {/* Eyes */}
        <mesh position={[0.12, 1.72, 0.28]}>
          <boxGeometry args={[0.07, 0.07, 0.03]} />
          <meshBasicMaterial color="#111827" />
        </mesh>
        <mesh position={[-0.12, 1.72, 0.28]}>
          <boxGeometry args={[0.07, 0.07, 0.03]} />
          <meshBasicMaterial color="#111827" />
        </mesh>
        {/* Whistle — small yellow cube on a black lanyard suggestion */}
        <mesh position={[0, 1.15, 0.24]}>
          <boxGeometry args={[0.09, 0.09, 0.09]} />
          <meshStandardMaterial
            color="#facc15"
            emissive="#facc15"
            emissiveIntensity={0.35}
          />
        </mesh>
        {/* Arms — pivot at shoulder, hanging when idle, swung up on goal */}
        <group ref={armLeftRef} position={[-0.5, 1.32, 0]}>
          <mesh castShadow position={[0, -0.35, 0]}>
            <boxGeometry args={[0.25, 0.75, 0.25]} />
            <meshStandardMaterial color="#ffffff" roughness={0.65} />
          </mesh>
        </group>
        <group ref={armRightRef} position={[0.5, 1.32, 0]}>
          <mesh castShadow position={[0, -0.35, 0]}>
            <boxGeometry args={[0.25, 0.75, 0.25]} />
            <meshStandardMaterial color="#ffffff" roughness={0.65} />
          </mesh>
        </group>
        {/* Legs — plain grey trousers */}
        <mesh castShadow position={[-0.18, 0.02, 0]}>
          <boxGeometry args={[0.26, 0.85, 0.28]} />
          <meshStandardMaterial color="#334155" roughness={0.7} />
        </mesh>
        <mesh castShadow position={[0.18, 0.02, 0]}>
          <boxGeometry args={[0.26, 0.85, 0.28]} />
          <meshStandardMaterial color="#334155" roughness={0.7} />
        </mesh>
      </group>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Floodlights — four tall corner poles with warm point lights so the arena
// stays visible at night without the player needing an avatar torch. The
// lights are always on (small distance, warm colour) — cheap and stable.
// ---------------------------------------------------------------------------
function Floodlights({ center, sport }) {
  const [ax, , az] = center || ARENA_CENTER;
  // Size the light square to the active sport's field/court.
  const fieldLen =
    sport === "football" ? FOOTBALL_FIELD.length
    : sport === "soccer" ? SOCCER_PITCH.length
    : BASKETBALL_COURT.length;
  const fieldWid =
    sport === "football" ? FOOTBALL_FIELD.width
    : sport === "soccer" ? SOCCER_PITCH.width
    : BASKETBALL_COURT.width;
  const dx = fieldLen / 2 + 1.6;
  const dz = fieldWid / 2 + 1.6;
  const corners = [
    [ax - dx, 0, az - dz],
    [ax + dx, 0, az - dz],
    [ax - dx, 0, az + dz],
    [ax + dx, 0, az + dz],
  ];
  const poleH = 6.5;
  return (
    <group>
      {corners.map(([x, , z], i) => (
        <group key={i} position={[x, 0, z]}>
          {/* Pole */}
          <mesh castShadow position={[0, poleH / 2, 0]}>
            <cylinderGeometry args={[0.09, 0.11, poleH, 8]} />
            <meshStandardMaterial color="#1e293b" roughness={0.6} />
          </mesh>
          {/* Lamp housing — chunky Roblox cube */}
          <mesh position={[0, poleH, 0]}>
            <boxGeometry args={[0.55, 0.4, 0.55]} />
            <meshStandardMaterial
              color="#f8fafc"
              emissive="#fff5c8"
              emissiveIntensity={1.15}
              roughness={0.35}
            />
          </mesh>
          {/* Actual point light — modest range so we don't wash out the whole quad */}
          <pointLight
            position={[0, poleH - 0.1, 0]}
            intensity={1.1}
            distance={14}
            decay={1.6}
            color="#fff5c8"
            castShadow={false}
          />
        </group>
      ))}
    </group>
  );
}

// ---------------------------------------------------------------------------
// SpectatorCam — a courtside camera puck the visitor can click to snap the
// camera into a broadcast-style low-angle sideline shot. Lives on the +Z
// sideline near half-court so it's discoverable from the default camera
// pose. When clicked, we teleport the OrbitControls to a fixed courtside
// pose that looks toward centre court.
// ---------------------------------------------------------------------------
function SpectatorCam({ arenaApi, center, sport }) {
  const three = useThree();
  const hovered = useRef(false);
  const glowRef = useRef();

  const [ax, , az] = center || ARENA_CENTER;
  const fieldLen =
    sport === "football" ? FOOTBALL_FIELD.length
    : sport === "soccer" ? SOCCER_PITCH.length
    : BASKETBALL_COURT.length;
  const fieldWid =
    sport === "football" ? FOOTBALL_FIELD.width
    : sport === "soccer" ? SOCCER_PITCH.width
    : BASKETBALL_COURT.width;
  // Position on the +Z sideline, clear of both courts and the referee.
  const pos = [
    ax + fieldLen / 2 - 2.5,
    0,
    az + fieldWid / 2 + 2.4,
  ];
  // Where we snap the camera when clicked — knee-height, angled at midcourt.
  const CAM_POS = [pos[0] - 2.2, 1.5, pos[2] + 1.6];
  const CAM_TARGET = [ax, 0.8, az];

  useFrame((state) => {
    if (!glowRef.current) return;
    const t = state.clock.elapsedTime;
    glowRef.current.material.emissiveIntensity =
      (hovered.current ? 1.4 : 0.9) + Math.sin(t * 3) * 0.15;
    glowRef.current.rotation.y = t * 0.35;
  });

  const snapToSpectator = () => {
    three.camera.position.set(CAM_POS[0], CAM_POS[1], CAM_POS[2]);
    const ctrl = three.controls;
    if (ctrl && ctrl.target && typeof ctrl.update === "function") {
      ctrl.target.set(CAM_TARGET[0], CAM_TARGET[1], CAM_TARGET[2]);
      ctrl.update();
    } else {
      three.camera.lookAt(CAM_TARGET[0], CAM_TARGET[1], CAM_TARGET[2]);
    }
    // Flip on the broadcast overlay skin. It auto-clears when the visitor
    // pans away from the spectator angle (see useFrame check below).
    if (arenaApi && arenaApi.setBroadcastActive) {
      arenaApi.setBroadcastActive(true);
    }
  };

  // Watch the camera each frame — when the visitor pans away from the
  // spectator sight-line by more than ~4 units of Euclidean distance, we
  // consider them "off broadcast" and clear the overlay.
  useFrame(() => {
    if (!arenaApi || !arenaApi.broadcastActive) return;
    const cp = three.camera.position;
    const drift = Math.hypot(cp.x - CAM_POS[0], cp.y - CAM_POS[1], cp.z - CAM_POS[2]);
    if (drift > 4.5) arenaApi.setBroadcastActive(false);
  });

  return (
    <group position={pos}>
      {/* Tripod pole */}
      <mesh castShadow position={[0, 0.9, 0]}>
        <cylinderGeometry args={[0.045, 0.055, 1.8, 8]} />
        <meshStandardMaterial color="#1e293b" roughness={0.6} />
      </mesh>
      {/* Base */}
      <mesh position={[0, 0.05, 0]}>
        <cylinderGeometry args={[0.25, 0.28, 0.1, 12]} />
        <meshStandardMaterial color="#334155" roughness={0.7} />
      </mesh>
      {/* Camera head — Roblox-blocky */}
      <group position={[0, 1.85, 0]}>
        <mesh
          ref={glowRef}
          castShadow
          onPointerOver={(e) => {
            e.stopPropagation();
            hovered.current = true;
            document.body.style.cursor = "pointer";
          }}
          onPointerOut={(e) => {
            hovered.current = false;
            document.body.style.cursor = "auto";
          }}
          onClick={(e) => {
            e.stopPropagation();
            snapToSpectator();
          }}
        >
          <boxGeometry args={[0.55, 0.4, 0.7]} />
          <meshStandardMaterial
            color="#0b1530"
            emissive="#e6b450"
            emissiveIntensity={0.9}
            roughness={0.4}
          />
        </mesh>
        {/* Lens — small emissive gold disc pointing toward the court */}
        <mesh position={[0, 0, -0.4]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.12, 0.14, 0.08, 16]} />
          <meshStandardMaterial
            color="#e6b450"
            emissive="#e6b450"
            emissiveIntensity={1.1}
            metalness={0.6}
            roughness={0.3}
          />
        </mesh>
        {/* Record dot — pulsing red */}
        <mesh position={[0.22, 0.12, 0.32]}>
          <sphereGeometry args={[0.045, 12, 12]} />
          <meshStandardMaterial
            color="#ef4444"
            emissive="#ef4444"
            emissiveIntensity={1.5}
          />
        </mesh>
      </group>
      {/* Nametag */}
      <group position={[0, 2.5, 0]}>
        <mesh>
          <boxGeometry args={[1.7, 0.36, 0.06]} />
          <meshStandardMaterial
            color="#0b1530"
            emissive="#e6b450"
            emissiveIntensity={0.5}
          />
        </mesh>
        <Text
          position={[0, 0, 0.04]}
          fontSize={0.18}
          color="#e6b450"
          anchorX="center"
          anchorY="middle"
        >
          SPECTATOR CAM
        </Text>
      </group>
    </group>
  );
}


// visitor's live position. Team-coloured torso so multiple visitors are
// distinguishable at a glance. No AI, no physics — purely presence-driven.
// ---------------------------------------------------------------------------
function PeerAvatar({ peer }) {
  const rootRef = useRef();
  const legLeftRef = useRef();
  const legRightRef = useRef();
  const currentPos = useRef(new THREE.Vector3(peer.pos[0], peer.pos[1], peer.pos[2]));
  const targetPos = useRef(new THREE.Vector3(peer.pos[0], peer.pos[1], peer.pos[2]));
  const lastMoveT = useRef(0);

  useEffect(() => {
    if (!peer.pos) return;
    targetPos.current.set(peer.pos[0], peer.pos[1], peer.pos[2]);
  }, [peer.pos]);

  useFrame((state, dt) => {
    const g = rootRef.current;
    if (!g) return;

    // Interpolate current toward target so movement between 5Hz updates
    // reads as continuous rather than teleporting.
    const before = currentPos.current.clone();
    currentPos.current.lerp(targetPos.current, Math.min(1, dt * 6));
    g.position.copy(currentPos.current);

    // Face movement direction when actually moving.
    const dx = currentPos.current.x - before.x;
    const dz = currentPos.current.z - before.z;
    const movedSq = dx * dx + dz * dz;
    if (movedSq > 1e-5) {
      g.rotation.y = Math.atan2(dx, dz);
      lastMoveT.current = state.clock.elapsedTime;
    }

    // Walk cycle only when moving.
    const walking = state.clock.elapsedTime - lastMoveT.current < 0.4;
    const t = state.clock.elapsedTime;
    const swing = walking ? Math.sin(t * 7) * 0.55 : 0;
    if (legLeftRef.current) legLeftRef.current.rotation.x = swing;
    if (legRightRef.current) legRightRef.current.rotation.x = -swing;
  });

  const color = getTeamColorById(peer.team || "royal");

  return (
    <group ref={rootRef}>
      {/* Torso — a bit translucent so peers are visibly "ghost" avatars */}
      <mesh position={[0, 0.9, 0]}>
        <boxGeometry args={[0.7, 0.9, 0.4]} />
        <meshStandardMaterial color={color} roughness={0.6} transparent opacity={0.9} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 1.65, 0]}>
        <boxGeometry args={[0.55, 0.55, 0.55]} />
        <meshStandardMaterial color="#f5d6b3" roughness={0.7} transparent opacity={0.9} />
      </mesh>
      {/* Eyes */}
      <mesh position={[0.12, 1.72, 0.28]}>
        <boxGeometry args={[0.07, 0.07, 0.03]} />
        <meshBasicMaterial color="#111827" />
      </mesh>
      <mesh position={[-0.12, 1.72, 0.28]}>
        <boxGeometry args={[0.07, 0.07, 0.03]} />
        <meshBasicMaterial color="#111827" />
      </mesh>
      {/* Arms */}
      <mesh position={[-0.5, 0.97, 0]}>
        <boxGeometry args={[0.25, 0.75, 0.25]} />
        <meshStandardMaterial color={color} roughness={0.6} transparent opacity={0.9} />
      </mesh>
      <mesh position={[0.5, 0.97, 0]}>
        <boxGeometry args={[0.25, 0.75, 0.25]} />
        <meshStandardMaterial color={color} roughness={0.6} transparent opacity={0.9} />
      </mesh>
      {/* Legs */}
      <group ref={legLeftRef} position={[-0.18, 0.44, 0]}>
        <mesh position={[0, -0.42, 0]}>
          <boxGeometry args={[0.26, 0.85, 0.28]} />
          <meshStandardMaterial color="#1e293b" roughness={0.7} transparent opacity={0.9} />
        </mesh>
      </group>
      <group ref={legRightRef} position={[0.18, 0.44, 0]}>
        <mesh position={[0, -0.42, 0]}>
          <boxGeometry args={[0.26, 0.85, 0.28]} />
          <meshStandardMaterial color="#1e293b" roughness={0.7} transparent opacity={0.9} />
        </mesh>
      </group>
      {/* Nametag — a small floating panel with the peer's display name.
          Rendered as pure 3D geometry (colored plane + drei <Text>) so it
          faces the camera via a billboard rotation and shows through the
          scene at any distance without needing DOM overlay plumbing. */}
      <NametagBillboard color={color} label={peer.name || "Guest"} y={2.4} />
    </group>
  );
}

// A small floating name-badge above the peer. We use a simple stacked
// arrangement of unlit `<meshBasicMaterial>` planes with `DoubleSide` so
// the badge is legible from both sides — this avoids the fragility of
// billboard rotation nested inside a parent group whose own rotation
// changes each frame from the walk-cycle.
//
// The name text is rendered by drei <Text> (Troika). If the font subset
// hasn't finished loading yet the mesh backdrop still shows, so the peer
// is always identifiable by team colour even before the text paints.
function NametagBillboard({ color, label, y = 2.5 }) {
  const labelText = String(label || "Guest").slice(0, 18);
  const width = Math.max(1.4, labelText.length * 0.22 + 0.7);
  return (
    <group position={[0, y, 0]}>
      {/* Wider coloured border — chunky Roblox-style */}
      <mesh>
        <boxGeometry args={[width + 0.12, 0.62, 0.08]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.7}
          roughness={0.4}
        />
      </mesh>
      {/* Dark inner pill */}
      <mesh position={[0, 0, 0.05]}>
        <boxGeometry args={[width, 0.5, 0.02]} />
        <meshStandardMaterial
          color="#0b1530"
          emissive="#0b1530"
          emissiveIntensity={0.35}
          roughness={0.5}
        />
      </mesh>
      {/* Team-colour dot on the left of the pill */}
      <mesh position={[-width / 2 + 0.22, 0, 0.08]}>
        <cylinderGeometry args={[0.14, 0.14, 0.02, 16]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={1.0}
        />
      </mesh>
      {/* Name text */}
      <Text
        position={[0.1, 0, 0.09]}
        fontSize={0.32}
        color="#f3ead4"
        anchorX="center"
        anchorY="middle"
      >
        {labelText}
      </Text>
    </group>
  );
}

// ---------------------------------------------------------------------------
// Main export — the SportsArena scene component.
// ---------------------------------------------------------------------------
export default function SportsArena({ arenaApi }) {
  const { sport, difficulty, addScore, lastGoal, teamColor, teamId, matchInvite, role, touchFireRef, teleportRef, derbyDone, setDerbyDone, swingDirRef } = arenaApi;
  const activeCenter = getSportCenter(sport);
  // The player role only affects football. For every other sport we treat
  // the visitor as the QB (i.e. click-to-shoot mechanics of the round balls).
  const effectiveRole = sport === "football" ? role : "qb";
  const roleRef = useRef(effectiveRole);
  roleRef.current = effectiveRole;
  // Read the latest matchInvite in refs so async callbacks (WebSocket
  // onmessage) always see the newest value without re-subscribing.
  const matchInviteRef = useRef(matchInvite);
  matchInviteRef.current = matchInvite;

  // Which NPC slot the visitor is currently controlling. `-1` = the visitor
  // is the QB (or the sport isn't football) and no slot is player-driven.
  // Populated by a useEffect further down but declared here so that
  // useOpponentAI (called before that effect) can read it in its useFrame.
  const controlledIndexRef = useRef(-1);
  const keysRef = useRef({ w: false, a: false, s: false, d: false });

  // Trigger the crowd cheer / opponent groan on every new goal, layered
  // with a short referee whistle-blow. A second countdown-whistle plays
  // just before the ball drops again (~800ms after the goal) so the pace
  // feels like a live match with proper restart cadence.
  useEffect(() => {
    if (!lastGoal) return;
    playWhistle(0);              // goal whistle
    playCheer(lastGoal.who);     // crowd reaction
    // Basketball: layer a short net-swoosh on the *player's* made bucket so
    // the score reads viscerally — the swoosh cuts through the cheer.
    if (sport === "basketball" && lastGoal.who === "player") {
      playSwoosh();
    }
    playWhistle(0.8);            // countdown whistle right before ball drop
  }, [lastGoal, sport]);

  const {
    ballRef,
    ready,
    kickBallFromPlayer,
    kickBallFromNpc,
    applyRemoteKick,
    getBallPosition,
    setBallState,
  } = useArenaPhysics({ sport, onGoal: addScore });

  // ------------------------------------------------------------------------
  // Baseball / Home Run Derby state machine.
  //   pitchPhaseRef  — 0..1, drives the PitcherNPC arm animation
  //   swingPhaseRef  — 0..1, drives the BaseballBatter bat animation
  //   pitchTimerRef  — countdown until the next pitch (starts a full cycle)
  //   pitchArmedRef  — true while the ball is in flight toward the plate
  //   swingArmedRef  — set for one frame after a swing; used for timing check
  //   pitchCountRef  — total pitches THIS game (max 10 pitches per session)
  //   hitFlightRef   — true from the moment of contact until the ball
  //                    either crosses the HR wall or lands (i.e. we're
  //                    waiting for the result). Prevents double-counting.
  // ------------------------------------------------------------------------
  const pitchPhaseRef = useRef(0);
  const swingPhaseRef = useRef(0);
  const pitchTimerRef = useRef(2.5); // 2.5s of "get ready" before first pitch
  const pitchArmedRef = useRef(false);
  const swingArmedRef = useRef(false);
  const swingArmedAtRef = useRef(0);
  const pitchCountRef = useRef(0);
  const hitFlightRef = useRef(false);
  const hitMaxDistRef = useRef(0);
  const [pitchCountView, setPitchCountView] = useState(0);
  const [homeRunsView, setHomeRunsView] = useState(0);
  const [outsView, setOutsView] = useState(0);
  const TOTAL_PITCHES = 10;
  const homeRunsRef = useRef(0);
  const outsRef = useRef(0);

  // Reset the derby when the sport changes to / away from baseball.
  useEffect(() => {
    if (sport !== "baseball") {
      pitchPhaseRef.current = 0;
      swingPhaseRef.current = 0;
      pitchTimerRef.current = 2.5;
      pitchArmedRef.current = false;
      swingArmedRef.current = false;
      pitchCountRef.current = 0;
      hitFlightRef.current = false;
      hitMaxDistRef.current = 0;
      homeRunsRef.current = 0;
      outsRef.current = 0;
      setPitchCountView(0);
      setHomeRunsView(0);
      setOutsView(0);
      setDerbyDone(false);
    }
  }, [sport]);

  // Register the swing action on touchFireRef when sport === "baseball" so
  // the mobile FIRE button + the desktop click both drive the same swing.
  const doSwing = useCallback(() => {
    if (sport !== "baseball") return false;
    if (derbyDone) return false;
    // Ignore back-to-back swings within 300ms (bat is still recovering).
    if (swingPhaseRef.current > 0 && swingPhaseRef.current < 0.8) return false;
    swingArmedRef.current = true;
    swingArmedAtRef.current = performance.now();
    swingPhaseRef.current = 0.01; // kick off animation from the top of the cycle
    return true;
  }, [sport, derbyDone]);

  // Difficulty scaling for the pitch interval and hit window.
  const pitchIntervalSec = useMemo(() => {
    switch (difficulty) {
      case "easy":       return 4.5;
      case "difficult":  return 3.0;
      case "hallOfFame": return 2.6;
      case "basic":
      default:           return 3.6;
    }
  }, [difficulty]);
  const hitWindowMs = useMemo(() => {
    switch (difficulty) {
      case "easy":       return 260;
      case "difficult":  return 150;
      case "hallOfFame": return 110;
      case "basic":
      default:           return 200;
    }
  }, [difficulty]);

  // Main baseball tick — runs only when sport === "baseball".
  useFrame((_, delta) => {
    if (sport !== "baseball" || !ready || !ballRef.current) return;
    // Extra guard during sport swaps — the ball body may be mid-rebuild.
    const bp0 = getBallPosition();
    if (!bp0 || typeof bp0.x !== "number") return;
    const [ax, , az] = SPORT_CENTERS.baseball;
    const cfg = BASEBALL_DIAMOND;
    const plateX = ax + cfg.plateOffset;

    // 1) Advance the pitcher's arm animation whenever a pitch is in-flight
    //    OR being wound up.
    if (pitchArmedRef.current || pitchPhaseRef.current > 0) {
      pitchPhaseRef.current += delta * 1.6;
      if (pitchPhaseRef.current > 1) pitchPhaseRef.current = 0;
    }

    // 2) Advance the batter's swing animation whenever a swing is armed.
    if (swingPhaseRef.current > 0) {
      swingPhaseRef.current += delta * 3.4;
      if (swingPhaseRef.current > 1) swingPhaseRef.current = 0;
    }

    // 3) Countdown to next pitch when no pitch is in flight.
    if (!pitchArmedRef.current && !hitFlightRef.current) {
      pitchTimerRef.current -= delta;
      if (pitchTimerRef.current <= 0 && !derbyDone) {
        if (pitchCountRef.current >= TOTAL_PITCHES) {
          // Derby is over — flag Game Over.
          setDerbyDone(true);
          return;
        }
        // Kick off a new pitch: reset ball to pitcher's hand and apply an
        // impulse toward home plate.
        pitchArmedRef.current = true;
        pitchPhaseRef.current = 0.01;
        pitchCountRef.current += 1;
        setPitchCountView(pitchCountRef.current);
        pitchTimerRef.current = pitchIntervalSec;

        // Snap ball to the pitcher's hand (central mound) and pitch toward
        // home plate (+X direction).
        const moundX = plateX - cfg.size / 2;
        const moundZ = az;
        // Position ball at pitcher's hand height (~1.7m) with a small +X
        // velocity toward home plate. Speed scales with difficulty.
        const speed = cfg.pitchSpeed + (
          difficulty === "difficult" ? 3 : difficulty === "hallOfFame" ? 5 : difficulty === "easy" ? -2 : 0
        );
        // Distance from mound to plate along +X
        const flightDist = plateX - moundX - 0.5;
        // Time-of-flight ≈ distance / speed
        const tof = flightDist / speed;
        // vy0 so the ball peaks and lands at plate height (~1.1m) —
        // simple projectile: y(t) = y0 + vy0 t - 0.5 g t^2
        const y0 = 1.7;
        const yTarget = 1.1;
        const g = 9.81; // Rapier default gravity
        const vy0 = (yTarget - y0) / tof + 0.5 * g * tof;

        // Snap the ball to the pitcher's hand + apply the pitch velocity
        // via the direct setBallState API (crisper than an impulse).
        setBallState(
          { x: moundX + 0.6, y: y0, z: moundZ },
          { x: speed, y: vy0, z: 0 },
          { x: 0, y: 0, z: 0 }
        );
      }
    }

    // 4) When the ball is in-flight AS a pitch, watch for it crossing the
    //    plate. If a swing was armed within the hit window, apply a
    //    hit-impulse; otherwise, once the ball passes the plate, count a
    //    strike (out) and reset for the next pitch.
    if (pitchArmedRef.current) {
      const bp = getBallPosition();
      // Ball has reached / passed the plate along +X
      const atPlate = bp.x >= plateX - 0.3;
      // We consider a swing "connecting" if it was armed within the last
      // hitWindowMs AND the ball is within ±1.5m of the plate on X.
      const now = performance.now();
      const armedRecently = swingArmedRef.current && (now - swingArmedAtRef.current) < hitWindowMs;
      const ballNearPlate = Math.abs(bp.x - plateX) < 1.8;
      if (armedRecently && ballNearPlate) {
        // HIT — apply a strong -X impulse + upward lift. Timing accuracy
        // determines the pull: perfect timing → dead centre; off-timing
        // pulls the ball toward foul territory.
        const timingErr = Math.abs(now - swingArmedAtRef.current) / hitWindowMs; // 0..1
        // Bat-path aim: swingDirRef is set by the ArenaTouchControls +
        // keyboard Q/A/E shortcuts. -1 = opposite field (right / +Z from
        // batter's POV), 0 = straightaway, +1 = pull (left / -Z).
        const aim = (swingDirRef && swingDirRef.current) || 0;
        const aimPull = aim * 5.5; // strong bias when aimed
        const noise = (Math.random() - 0.5) * 2 * (timingErr * 3 + 0.6);
        const pullZ = aimPull + noise;
        const power = 16 - timingErr * 4; // less power on late/early swings
        setBallState(
          null,
          { x: -power, y: 8.5, z: pullZ },
          { x: 5, y: 2, z: 0 }
        );
        playBatCrack();
        pitchArmedRef.current = false;
        hitFlightRef.current = true;
        hitMaxDistRef.current = 0;
        swingArmedRef.current = false;
      } else if (atPlate && bp.x > plateX + 1.5) {
        // Ball passed the plate without contact — strike/out.
        pitchArmedRef.current = false;
        swingArmedRef.current = false;
        outsRef.current += 1;
        setOutsView(outsRef.current);
        addScore("cpu");
        pitchTimerRef.current = 1.4;
      }
    }

    // 5) If a hit is in flight, track the ball's max distance from home
    //    plate and detect the outcome: HR if it crosses the HR wall while
    //    still airborne, out if it lands short.
    if (hitFlightRef.current) {
      const bp = getBallPosition();
      const dist = plateX - bp.x; // ball flies -X after a hit
      if (dist > hitMaxDistRef.current) hitMaxDistRef.current = dist;
      const past = dist > cfg.homeRunDistance;
      const grounded = bp.y < 0.35;
      // Also detect a foul (ball landed way off the field line)
      const foul = Math.abs(bp.z - az) > 14;
      if (past && bp.y > 0.6) {
        // HOME RUN
        homeRunsRef.current += 1;
        setHomeRunsView(homeRunsRef.current);
        addScore("player");
        hitFlightRef.current = false;
        pitchTimerRef.current = 1.6;
      } else if (grounded || foul) {
        // Fly-out or foul: count as an out.
        outsRef.current += 1;
        setOutsView(outsRef.current);
        addScore("cpu");
        hitFlightRef.current = false;
        pitchTimerRef.current = 1.6;
      }
    }
  });

  // Wire the touch-fire path for baseball: swings replace ball-kicks.
  // The main touchFireRef registration below stays in place for other
  // sports; here we simply intercept when sport === "baseball".
  useEffect(() => {
    if (sport !== "baseball" || !touchFireRef) return;
    touchFireRef.current = { fire: () => doSwing() };
    return () => {
      if (touchFireRef.current) touchFireRef.current = null;
    };
  }, [sport, touchFireRef, doSwing]);


  // -----------------------------------------------------------------------
  // Multiplayer presence — broadcasts this visitor's camera position + team
  // + selected sport so other visitors' clients can render them as ghost
  // avatars. `getMyState` reads the live camera position from three.js each
  // send tick so we don't have to re-render on every position change.
  // -----------------------------------------------------------------------
  const three = useThree();
  const myStateFn = useMemo(
    () => () => {
      const p = three.camera.position;
      return {
        pos: [p.x, 0, p.z], // ground-plane projection is what other viewers see
        team: teamId,
        sport,
        invite: !!matchInvite,
      };
    },
    [three, teamId, sport, matchInvite]
  );

  // Handle incoming kicks from remote peers. If MY match-invite is ON, apply
  // the same impulse to my local ball so both players' clicks influence a
  // shared-feeling game. If it's OFF, ignore — the peer's click is
  // co-presence only.
  const handleRemoteKick = useCallback(
    (kick) => {
      if (!matchInviteRef.current) return;
      // Only honour kicks that match the sport we're currently playing —
      // avoids random impulses if peers are on different sports.
      if (kick.sport && kick.sport !== sport) return;
      applyRemoteKick(kick.dir, kick.power || 5);
    },
    [applyRemoteKick, sport]
  );

  const presence = useArenaPresence({
    name: "Guest",
    getMyState: myStateFn,
    onKick: handleRemoteKick,
  });
  // Expose the peer count back to the HUD via arenaApi so the pill can
  // show a "N others in arena" indicator. The ArenaHUD reads
  // arenaApi._presencePeerCount if present. useEffect is used so we don't
  // mutate arenaApi during render.
  useEffect(() => {
    arenaApi._setPeerCount && arenaApi._setPeerCount(presence.peerCount);
  }, [arenaApi, presence.peerCount]);
  useEffect(() => {
    arenaApi._setReadyCount && arenaApi._setReadyCount(presence.readyCount);
  }, [arenaApi, presence.readyCount]);

  // Vector3 refs for NPCs. Layout depends on sport:
  //   • basketball / soccer  → 4 CPU opponents
  //   • football             → 6 teammates (offense) + 7 defenders = 13 total
  const npcSlotCount = sport === "football" ? 13 : 4;
  const npcPositions = useMemo(
    () => Array.from({ length: 13 }, () => ({ current: null })),
    []
  );

  // Initialise positions on first mount / sport change so opponents spawn on
  // the PLAYER's side of the pitch (they attack that goal). Two forwards +
  // two defenders behind, spread across the Z axis.
  useEffect(() => {
    const [ax, , az] = getSportCenter(sport);
    let layout;
    if (sport === "basketball") {
      layout = [
        { pos: [ax + 3.0, 0, az - 2.2], role: "defender" },
        { pos: [ax + 3.0, 0, az + 2.2], role: "defender" },
        { pos: [ax + 5.5, 0, az - 1.1], role: "defender" },
        { pos: [ax + 5.5, 0, az + 1.1], role: "defender" },
      ];
    } else if (sport === "football") {
      // 6 teammates (roles: receivers spread across the field on -X side of
      // the line of scrimmage; a couple of blockers close to QB) + 7
      // defenders shifted forward on +X side, spread across the field.
      layout = [
        // Teammates (offense): receivers spread across Z axis, near QB
        { pos: [ax - 1.5, 0, az - 4.0], role: "teammate", route: "slant" },
        { pos: [ax - 1.5, 0, az + 4.0], role: "teammate", route: "slant" },
        { pos: [ax - 2.0, 0, az - 2.2], role: "teammate", route: "go" },
        { pos: [ax - 2.0, 0, az + 2.2], role: "teammate", route: "go" },
        { pos: [ax - 3.5, 0, az - 0.7], role: "teammate", route: "block" },
        { pos: [ax - 3.5, 0, az + 0.7], role: "teammate", route: "block" },
        // Defenders on the CPU end (they blitz toward QB / cover receivers)
        { pos: [ax + 2.5, 0, az - 5.0], role: "defender" },
        { pos: [ax + 2.5, 0, az + 5.0], role: "defender" },
        { pos: [ax + 4.0, 0, az - 2.5], role: "defender" },
        { pos: [ax + 4.0, 0, az + 2.5], role: "defender" },
        { pos: [ax + 6.0, 0, az - 4.0], role: "defender" },
        { pos: [ax + 6.0, 0, az + 4.0], role: "defender" },
        { pos: [ax + 6.5, 0, az], role: "defender" },
      ];
    } else if (sport === "baseball") {
      // Home Run Derby has no roaming NPCs — the pitcher is a static
      // component and the batter is the visitor's avatar. Park everything
      // off-field so nothing appears on the diamond.
      layout = [];
    } else {
      // soccer
      layout = [
        { pos: [ax + 3.5, 0, az - 3.2], role: "defender" },
        { pos: [ax + 3.5, 0, az + 3.2], role: "defender" },
        { pos: [ax + 6.5, 0, az - 1.6], role: "defender" },
        { pos: [ax + 6.5, 0, az + 1.6], role: "defender" },
      ];
    }
    for (let i = 0; i < 13; i++) {
      if (i >= layout.length) {
        // Park unused slots off-field so nothing lingers on screen.
        npcPositions[i].current = null;
        continue;
      }
      const item = layout[i];
      const v = new THREE.Vector3(item.pos[0], item.pos[1], item.pos[2]);
      v.facing = Math.PI;
      v.role = item.role;
      v.route = item.route || null;
      v.startPos = [item.pos[0], item.pos[1], item.pos[2]]; // remembered for football routes
      npcPositions[i].current = v;
    }
  }, [sport, npcPositions]);

  useOpponentAI({
    npcPositions,
    ready,
    sport,
    difficulty,
    getBallPosition,
    kickBallFromNpc,
    controlledIndexRef,
  });

  // Football snap phase state — 0 = pre-snap (click to snap), 1 = QB holds
  // the ball (click to throw). After a throw we return to phase 0 once the
  // ball has come to rest or a goal has been scored (handled in resetBall).
  const snapPhaseRef = useRef(0);
  useEffect(() => {
    snapPhaseRef.current = 0;
  }, [sport]);
  useEffect(() => {
    // Any goal takes us back to pre-snap so the next play starts fresh.
    snapPhaseRef.current = 0;
  }, [lastGoal]);

  const handleKick = (worldPoint, cameraPos) => {
    // Ignore clicks when physics isn't ready yet.
    if (!ready) return;

    // --- BASEBALL: any click on the diamond is a swing (same as touch FIRE) ---
    if (sport === "baseball") {
      doSwing();
      return;
    }

    const ballPos = getBallPosition();

    // --- FOOTBALL: two-click snap → throw mechanic ---
    if (sport === "football") {
      if (snapPhaseRef.current === 0) {
        // Snap: teleport the ball into the QB's hand (just in front of the
        // camera). Small upward velocity so it visibly rises to QB.
        const forward = new THREE.Vector3(
          worldPoint.x - cameraPos.x,
          0,
          worldPoint.z - cameraPos.z
        );
        if (forward.lengthSq() < 0.001) forward.set(1, 0, 0);
        forward.normalize();
        // QB "hand" position — 1m forward, 1.2m off the ground.
        const bx = cameraPos.x + forward.x * 0.9;
        const bz = cameraPos.z + forward.z * 0.9;
        if (ballRef.current) {
          const body = ballRef.current.__rapierBody || null;
        }
        // Move the ball body — the Rapier body ref lives inside the hook,
        // so we teleport via resetBall's parent-friendly path: applyImpulse
        // with a manual translation via a helper on the hook (kickBallFrom-
        // Player writes impulse; direct teleport isn't exposed). Trick: use
        // an upward impulse strong enough to lift + arc to the QB hand.
        // Since implementing a full teleport API in the hook takes more
        // ceremony, we cheat: just apply a large upward impulse so the ball
        // pops into the air where the QB can throw it on the next click.
        kickBallFromPlayer(
          new THREE.Vector3(bx - 0.3, 0.5, bz),
          new THREE.Vector3(bx, 3, bz)
        );
        snapPhaseRef.current = 1;
        return;
      }
      // Phase 1 — throw. Apply impulse toward the click point; the goal-box
      // AABB already covers the CPU end zone so a completion into it counts
      // as a touchdown automatically.
      kickBallFromPlayer(cameraPos, worldPoint);
      snapPhaseRef.current = 0;
      return;
    }

    // --- BASKETBALL / SOCCER: original click-to-kick semantics ---
    const dx = cameraPos.x - ballPos.x;
    const dz = cameraPos.z - ballPos.z;
    const dist = Math.hypot(dx, dz);
    if (dist > PLAYER_KICK_RADIUS + 4) return; // silently ignore far kicks

    kickBallFromPlayer(cameraPos, worldPoint);

    // If Match Invite is ON, broadcast the same kick so other peers who
    // have accepted the invite can apply the impulse to their ball. We
    // reconstruct the same direction the physics hook uses (ball-away-from
    // -player blended toward the click target) so remote balls travel in a
    // similar arc.
    if (matchInviteRef.current) {
      const sportCfg = SPORTS[sport];
      const baseX = ballPos.x - cameraPos.x;
      const baseZ = ballPos.z - cameraPos.z;
      const baseLen = Math.hypot(baseX, baseZ) || 1;
      let dirX = baseX / baseLen;
      let dirZ = baseZ / baseLen;
      const aimX = worldPoint.x - ballPos.x;
      const aimZ = worldPoint.z - ballPos.z;
      const aimLen = Math.hypot(aimX, aimZ);
      if (aimLen > 0.001) {
        const ax = aimX / aimLen;
        const az = aimZ / aimLen;
        dirX = dirX * 0.45 + ax * 0.55;
        dirZ = dirZ * 0.45 + az * 0.55;
        const l = Math.hypot(dirX, dirZ) || 1;
        dirX /= l;
        dirZ /= l;
      }
      presence.sendKick({
        dir: [dirX, 0, dirZ],
        power: sportCfg.playerKickImpulse,
        sport,
      });
    }
  };

  // -----------------------------------------------------------------------
  // Role-driven player-controlled slot (football only).
  //
  // When the visitor plays WR / RB / DB we route their input to one of the
  // NPC slots (index 0/4 for teammates, index 6 for defenders) — the AI
  // stops moving that slot, WASD/arrow keys write to it, and the camera
  // follows over-the-shoulder. controlledIndexRef is declared above so the
  // AI hook can read it; here we only update its value on role/sport change.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (effectiveRole === "wr" || effectiveRole === "rb") {
      controlledIndexRef.current = effectiveRole === "wr" ? 0 : 4;
    } else if (effectiveRole === "db") {
      controlledIndexRef.current = 6;
    } else {
      controlledIndexRef.current = -1;
    }
  }, [effectiveRole, sport]);

  // Keyboard input for the controlled slot.
  useEffect(() => {
    if (effectiveRole === "qb") return undefined;
    const onKey = (e, down) => {
      const k = e.key.toLowerCase();
      if (k === "w" || k === "arrowup") keysRef.current.w = down;
      else if (k === "s" || k === "arrowdown") keysRef.current.s = down;
      else if (k === "a" || k === "arrowleft") keysRef.current.a = down;
      else if (k === "d" || k === "arrowright") keysRef.current.d = down;
    };
    const kd = (e) => onKey(e, true);
    const ku = (e) => onKey(e, false);
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    return () => {
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
    };
  }, [effectiveRole]);

  // Auto-CPU-QB: when the visitor isn't the QB, a virtual quarterback throws
  // the ball toward the player-controlled slot on a periodic interval.
  const nextThrowAtRef = useRef(0);

  useFrame((_, dt) => {
    // Move the player-controlled slot via WASD input.
    const ci = controlledIndexRef.current;
    if (ci >= 0) {
      const pos = npcPositions[ci]?.current;
      if (pos) {
        const speed = 6.5; // m/s — a bit faster than AI so control feels responsive
        let dx = 0;
        let dz = 0;
        if (keysRef.current.w) dz -= 1;
        if (keysRef.current.s) dz += 1;
        if (keysRef.current.a) dx -= 1;
        if (keysRef.current.d) dx += 1;
        const len = Math.hypot(dx, dz);
        if (len > 0) {
          dx /= len;
          dz /= len;
          pos.x += dx * speed * dt;
          pos.z += dz * speed * dt;
          pos.facing = Math.atan2(dx, dz);
        }
        // Camera follow — third-person, slightly elevated behind the
        // controlled character with a downward look so the field stays
        // in frame. `facing` is atan2(dx, dz), so behind = subtract in
        // that direction with a longer offset for better field awareness.
        const behindX = -Math.sin(pos.facing) * 5.5;
        const behindZ = -Math.cos(pos.facing) * 5.5;
        three.camera.position.lerp(
          new THREE.Vector3(pos.x + behindX, 3.5, pos.z + behindZ),
          Math.min(1, dt * 3.5)
        );
        const ctrl = three.controls;
        if (ctrl && ctrl.target) {
          ctrl.target.lerp(new THREE.Vector3(pos.x, 1.0, pos.z), Math.min(1, dt * 5));
          ctrl.update && ctrl.update();
        } else {
          three.camera.lookAt(pos.x, 1.0, pos.z);
        }
      }
    }

    // Auto CPU-QB throws when the player is not the QB.
    if (effectiveRole !== "qb" && effectiveRole !== "db" && sport === "football" && ready) {
      const now = performance.now();
      if (now > nextThrowAtRef.current) {
        // Aim at the controlled receiver's current position, blended with
        // some downfield offset so it feels like a real pass ahead.
        const target = npcPositions[controlledIndexRef.current]?.current;
        if (target) {
          const [ax, , az] = getSportCenter(sport);
          const leadX = target.x - 1.5; // lead ahead toward CPU end zone
          const targetPos = [leadX, 1.6, target.z];
          // Snap ball toward QB position (arena centre-x, slight offset)
          const qbPos = new THREE.Vector3(ax, 1.6, az);
          kickBallFromPlayer(
            new THREE.Vector3(qbPos.x - 1, 0.5, qbPos.z),
            new THREE.Vector3(targetPos[0], targetPos[1], targetPos[2])
          );
        }
        nextThrowAtRef.current = now + 4200; // next throw in ~4.2s
      }
    }
    // For DB: no auto-QB throws needed from the player side; the CPU offence
    // does its own thing (the ball starts at centre; defenders converge).

    // -------------------------------------------------------------------
    // Football only — interception + snap-to-hand catch checks.
    //
    //  • If ANY defender is within 0.7m of the ball and the ball is above
    //    the ground (still in flight) → +1 CPU, reset ball.
    //  • If ANY teammate is within 1.5m of the ball and the ball has
    //    descended below 1.6m (i.e. it's landing) → the teammate "catches":
    //    ball snaps to their hand, they get a quick +0.6m Y bounce, and we
    //    fire a small forward impulse on the ball so it continues toward
    //    the CPU end zone (the goal-box AABB completes the TD).
    // -------------------------------------------------------------------
    if (sport === "football" && ready) {
      // Only run interception / catch checks while a play is in progress.
      // The snap phase starts at 0 (pre-snap) and flips to 1 as soon as
      // the QB throws or the ball has been in the air for more than half
      // a second. During pre-snap / dead-ball we skip these checks so the
      // ball can settle without instantly triggering a stop.
      const ballPos = getBallPosition();
      const [fcx, , fcz] = getSportCenter("football");
      if (ballPos.y > 1.0 || Math.abs(ballPos.x - fcx) > 3 || Math.abs(ballPos.z - fcz) > 3) {
        for (let i = 0; i < npcPositions.length; i++) {
          const p = npcPositions[i]?.current;
          if (!p) continue;
          const dx = p.x - ballPos.x;
          const dz = p.z - ballPos.z;
          const dSq = dx * dx + dz * dz;
          if (p.role === "defender" && dSq < 0.7 * 0.7 && ballPos.y > 0.5) {
            addScore("cpu");
            playWhistle(0);
            playCheer("cpu");
            break;
          }
          if (p.role === "teammate" && dSq < 1.5 * 1.5 && ballPos.y < 1.6 && ballPos.y > 0.3) {
            kickBallFromNpc(
              new THREE.Vector3(p.x, p.y, p.z),
              [fcx - FOOTBALL_FIELD.length / 2 + 1, 0.5, p.z],
              1.3,
              0.05
            );
            break;
          }
        }
      }
    }
  });

  // Register a `fire` function on arenaApi.touchFireRef so the mobile
  // ArenaTouchControls can trigger a kick/throw without a pointer event.
  // The synthesized world point is 6m in front of the camera on the XZ plane
  // so the ball travels in the direction the visitor is looking, matching
  // the desktop click-to-kick semantics.
  useEffect(() => {
    if (!touchFireRef) return;
    touchFireRef.current = {
      fire: () => {
        const cam = three.camera;
        const camPos = cam.position.clone();
        const dir = new THREE.Vector3();
        cam.getWorldDirection(dir);
        dir.y = 0;
        if (dir.lengthSq() < 0.0001) dir.set(1, 0, 0);
        dir.normalize();
        const worldPoint = new THREE.Vector3(
          camPos.x + dir.x * 6,
          0,
          camPos.z + dir.z * 6
        );
        handleKick(worldPoint, camPos);
      },
    };
    return () => {
      if (touchFireRef.current) touchFireRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchFireRef, sport, ready]);

  // Register a teleport function on arenaApi.teleportRef so the HUD's "Go
  // to Arena" button can snap both the orbit camera AND (if the visitor is
  // walking) their avatar to the entrance plaza. `to` is a [x,y,z] world
  // point; camera pulls back onto a nice orbit anchor a few metres away.
  useEffect(() => {
    if (!teleportRef) return;
    teleportRef.current = {
      teleport: (to) => {
        const [tx, , tz] = to || ENTRANCE_CENTER;
        const cam = three.camera;
        // Nice cinematic entry pose: camera pulled back +Z and lifted so
        // the visitor sees all three sports fan out from the plaza.
        cam.position.set(tx + 0.2, 12.5, tz + 22);
        const ctrl = three.controls;
        if (ctrl && ctrl.target && typeof ctrl.update === "function") {
          ctrl.target.set(tx, 1.2, tz);
          ctrl.update();
        } else {
          cam.lookAt(tx, 1.2, tz);
        }
        // Drop the walker at the plaza too, if walk mode is engaged. The
        // walker keeps its own transform via window.__zone_walker_teleport,
        // wired in ThirdPersonWalker; the ZoneScene registers/unregisters
        // that handle on mode change so it's a no-op in orbit mode.
        try {
          if (typeof window.__zone_walker_teleport === "function") {
            window.__zone_walker_teleport(tx, 0, tz);
          }
        } catch (e) {
          /* ignore — walker not mounted */
        }
      },
    };
    return () => {
      if (teleportRef.current) teleportRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teleportRef]);

  // Auto-Camera Follow — when the visitor changes sport in the HUD, glide
  // the camera to the new field's centre so they can immediately see (and
  // interact with) the active game. We skip the very first mount so the
  // camera doesn't yank away from wherever the visitor initially opened
  // the app. Also publish the camera position to arenaApi.playerPosRef so
  // the bottom-right LiveMinimap can render a live "You Are Here" dot.
  const firstSportRef = useRef(true);
  useEffect(() => {
    if (firstSportRef.current) {
      firstSportRef.current = false;
      return;
    }
    if (teleportRef && teleportRef.current && typeof teleportRef.current.teleport === "function") {
      teleportRef.current.teleport(getSportCenter(sport));
    }
  }, [sport, teleportRef]);

  // Publish live camera position to arenaApi.playerPosRef every frame so
  // the DOM minimap can read it via a lightweight interval poll without
  // wiring itself into the react-three-fiber render loop.
  useFrame(() => {
    if (arenaApi && arenaApi.playerPosRef) {
      const cam = three.camera;
      arenaApi.playerPosRef.current = { x: cam.position.x, y: cam.position.y, z: cam.position.z };
    }
  });

  return (
    <group>
      {/* Central entrance plaza — the fixed teleport destination for the
          HUD's "Go to Arena" button. Sits at ENTRANCE_CENTER = [40,0,40]
          and marks the hub the three sport fields fan out from. */}
      <EntrancePlaza />

      {/* Gold chevron pathways from the plaza to each sport — wayfinding
          for first-time visitors so they know which way leads where. */}
      <SportPathways />

      {/* 3D signposts at path intersections pointing to each sport */}
      <SportSignposts />

      {/* Tall banner signage at the terminus of each pathway — reads at
          a glance from anywhere on the quad. */}
      <SportSignageBanners />

      {/* Floodlights follow the active sport so night play looks lit. */}
      <Floodlights center={activeCenter} sport={sport} />

      {/* Spectator crowd + bleacher stands (culled when player is far) —
          baseball has its own V-shape bleachers built into BaseballDiamond
          so we skip the generic crowd there. */}
      {sport !== "baseball" && (
        <SpectatorCrowd sport={sport} lastGoal={lastGoal} center={activeCenter} />
      )}

      {/* All three fields render at all times, each at its own anchor. The
          player-side goal on the ACTIVE sport inherits the team colour so
          the visitor can tell the two goals apart on the current field. */}
      <BasketballCourt teamColor={sport === "basketball" ? teamColor : "#ffffff"} />
      <SoccerPitch teamColor={sport === "soccer" ? teamColor : "#ffffff"} />
      <FootballField teamColor={sport === "football" ? teamColor : "#ffffff"} />
      <BaseballDiamond teamColor={sport === "baseball" ? teamColor : "#ffffff"} />

      {/* Baseball-only actors — pitcher on the mound + batter at home plate
          + a floating HR scoreboard. Only mount them when baseball is the
          active sport, since they animate every frame. */}
      {sport === "baseball" && (
        <>
          <PitcherNPC phaseRef={pitchPhaseRef} />
          <BaseballBatter swingPhaseRef={swingPhaseRef} teamColor={teamColor} />
          <HomeRunSign
            playerScore={homeRunsView}
            cpuScore={outsView}
            pitchCount={pitchCountView}
            totalPitches={TOTAL_PITCHES}
          />
        </>
      )}

      {/* Ball */}
      <Ball ref={ballRef} sport={sport} />

      {/* NPCs — jersey colour depends on role (football: teammates in the
          player's team colour, defenders in the opposing CPU colour). For
          basketball/soccer everyone's an opponent so all use CPU colours. */}
      {npcPositions.map((posRef, i) => {
        const posVec = posRef.current;
        const isTeammate = posVec && posVec.role === "teammate";
        const jersey = isTeammate
          ? teamColor
          : TEAM_COLORS.cpu[i % TEAM_COLORS.cpu.length];
        // Hide off-field slots (unused NPC indices when sport doesn't need 13).
        if (!posVec) return null;
        return (
          <OpponentNPC
            key={i}
            index={i}
            jerseyColor={jersey}
            positionRef={posRef}
          />
        );
      })}

      {/* Ambient referee — reacts (arms up) on every goal */}
      <Referee lastGoal={lastGoal} center={activeCenter} sport={sport} />

      {/* Remote visitors on the same court — ghost avatars via WebSocket presence */}
      {Object.entries(presence.peers).map(([pid, peer]) => (
        <PeerAvatar key={pid} peer={peer} />
      ))}

      {/* Signage + goal flash */}
      <ArenaSign sport={sport} center={activeCenter} />
      <GoalFlash lastGoal={lastGoal} center={activeCenter} />

      {/* Courtside spectator cam — click to teleport into a broadcast shot */}
      <SpectatorCam arenaApi={arenaApi} center={activeCenter} sport={sport} />

      {/* Invisible click catcher for player kicks */}
      <ArenaClickPlane onKick={handleKick} center={activeCenter} sport={sport} />
    </group>
  );
}
